import { useState, useEffect, useMemo, useRef, useId } from 'react';
import { ArrowLeft, Calendar, Lock, Trophy, RotateCcw, AlertCircle } from 'lucide-react';
import {
  BOOKING_DEFAULT_CONFIG,
  BOOKING_SOURCE_STYLES,
  bookingSourceStyle,
  calcCourtBookingPrice,
  courtDisplayName,
  courtOutOfServiceState,
  expandRecurrence,
  findFreeSlots,
  getBookingsForDay,
  isHoliday,
  resolveBookingWindowDays,
  validateBooking,
  resolveEffectiveCourts,
} from '../../engine';
import { toMinHHMM, fromMinHHMM } from '../../lib/date';
import { errMsg } from '../../lib/errors';
import { clearCourtOutOfService, setCourtOutOfService } from '../../lib/api/courts';
import { Storage } from '../../lib/storage';
import { isBookingNotRead } from '../../lib/bookingConfigRead';
import { priceSaveFailureMessage, runPriceSave } from './priceEditorSave';
import type { PriceSaveStep } from './priceEditorSave';
import { useLocale, errorMessage, knownErrorCodes } from '../../i18n';
import { AssetEditor, assetKindLabel } from './AssetEditor';
import { AssetBookingModal, assetBookingErrorMessage } from './AssetBookingModal';
import { assetBookingsForDay, availableAssetsFor } from '../../engine';
import { assetBookingsAttachedTo } from '../../lib/api/assetBookings';
import { BookingConfigEditor } from './BookingConfigEditor';
import { BookingEditModal } from './BookingEditModal';
import { refundStaffBooking, type StaffRefundDoor } from '../../lib/api/staffRefund';
import { BookingHelpModal } from './BookingHelpModal';
import { BookingPaymentModal } from './BookingPaymentModal';
import { BookingZoneEditor } from './BookingZoneEditor';
import { CalendarPopover } from './CalendarPopover';
import { CourtFacilityIcons } from './CourtFacilityIcons';
import { DEMO_BOOKER_NAMES } from './demoBookerNames';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface BookingViewProps {
  /**
   * Whether the club's STORED booking config has opening hours. False and the
   * calendar says booking is not open until they are saved — every booking door
   * on the server refuses before that (owner decision 2026-10-31, #1192). Null
   * or absent: not known, and nothing is said.
   */
  bookingHoursSaved?: boolean | null;
  /**
   * False while the venue setup (`booking_config_v1`) has not been read — the
   * read failed, or has not answered. Saving it is refused meanwhile (#1359):
   * with no version recorded the write would go out with no precondition and
   * could overwrite newer settings. The calendar says so and offers a re-read.
   * Absent: treated as read (screens that do not edit the setup).
   */
  bookingConfigRead?: boolean;
  /**
   * False while the price zones (`booking_zones_v1`) have not been read, and
   * the same for the guest prices (`booking_guest_prices_v1`) below (#1362).
   * The price editor writes both, and the venue setup's default price, so it is
   * closed while ANY of the three is unread, and its save checks all three
   * before it writes any. Absent: treated as read.
   */
  bookingZonesRead?: boolean;
  bookingGuestPricesRead?: boolean;
  /**
   * True when a document is unread because a SAVE of it failed (#1370), not a
   * read: the banner then says a save did not go through. Absent: false.
   */
  bookingSaveFailed?: boolean;
  /**
   * «Last inn på nytt»: read the unread booking settings again (#1359, #1362);
   * success unlocks saving.
   */
  onReloadConfig?: (() => Promise<{ success: boolean }>) | null;
  /** All bookings (tenant-scoped) — the calendar/slot views and validation read from this. */
  bookings?: any[];
  /** Optional date (YYYY-MM-DD) to land on; ignored if it is in the past. */
  initialDate?: string | null;
  /** Physical court count the center has registered (config.courtCount can override). */
  totalCourts?: number;
  /** Price zones used to compute live prices and the slot/cell labels. */
  zones?: any[];
  /** Booking config (gap rule, durations, opening hours, court meta, …). */
  config?: any;
  /** Series-reservation context: discount, extended window and the match→booking link. */
  serieContext?: any;
  /** Persists the edited price zones. */
  onUpdateZones?: (zones: any[]) => Promise<any> | any;
  /** Persists the edited booking config. */
  onUpdateConfig?: (config: any) => Promise<any> | any;
  /** The venue's guest default price document (#833), or null when none is stored. */
  guestPrices?: { guestDefaultPricePerHour?: number | null } | null;
  /** Persists it — `booking_guest_prices_v1`, under `booking.rules.manage`. */
  onUpdateGuestPrices?: (doc: { guestDefaultPricePerHour: number | null }) => Promise<any> | any;
  /**
   * The club's config AFTER a court's physical state changed (#271).
   *
   * Separate from `onUpdateConfig` on purpose: that PERSISTS a document the
   * admin edited, and this one has already been written by the server command.
   * Routing it through the config save would write the document straight back
   * — the one thing the server's ownership of the subtree exists to prevent.
   */
  onCourtStateChanged?: ((config: any) => void) | null;
  /** Navigates back (home, or the series program when in serieContext). */
  onBack: () => void;
  /** Creates a single booking; returns { success, error?, id?, booking? }. */
  onAddBooking: (data: any, opts?: any) => Promise<any>;
  /** Creates many bookings in one batch (recurring/multi-court). */
  onAddManyBookings?: (dataList: any[], opts?: any) => Promise<any>;
  /** Deletes many bookings in one batch (clear-all / series cancel). */
  onDeleteManyBookings?: (ids: string[]) => Promise<any>;
  /** Rebuilds a whole series from a new data list (series edit). */
  onReplaceSeries?: (seriesId: string, dataList: any[], opts?: any) => Promise<any>;
  /** Updates a single booking. */
  onUpdateBooking: (id: string, data: any, opts?: any) => Promise<any>;
  /** Deletes a single booking. */
  onDeleteBooking: (id: string) => Promise<any>;
  /**
   * «Avbestill og refunder» (#284): offered on a PAID booking when present and
   * `allowed` — `refundRight()` in the shell, from the `/me` union. Absent or
   * null is no button: a door is drawn only on a known yes.
   */
  staffRefund?: StaffRefundDoor | null;
  /** Opens the tournament/series a serie-match booking belongs to. */
  onOpenTournament?: ((seriesId: string, serieMatchId?: string | null) => void) | null;
  /**
   * Live player DB — enables the optional "register a match on this booking"
   * step in the customer create modal. When omitted, the step is hidden.
   */
  players?: any[];
  /** Optional: persist a brand-new player created inline from the match picker. */
  onPersistNewPlayer?: ((player: any) => Promise<void> | void) | null;
  /** Registers a planned single match against a created booking id. */
  onRegisterPlannedMatch?: ((planned: any) => Promise<any> | any) | null;
  /** Creates a single-match activity (via-aktiviteter variant) linked to a booking. */
  onCreateLinkedActivity?:
    ((args: { activityId: string; config: any }) => Promise<any> | any) | null;
  /** Planned single matches — used to tag their bookings as "Enkeltmatch" in the calendar. */
  plannedMatches?: any[];
  // ── Equipment beside the courts (docs/domains/ASSET_BOOKING.md) ──
  /** The registry: what this venue keeps and what the organization owns elsewhere. */
  assets?: any[];
  /** Every asset booking in this venue's calendar. */
  assetBookings?: any[];
  /** Books one asset; the intent names the court booking it rides with, if any. */
  onAddAssetBooking?: ((intent: any) => Promise<any>) | null;
  onUpdateAssetBooking?: ((id: string, patch: any) => Promise<any>) | null;
  onDeleteAssetBooking?: ((id: string) => Promise<any>) | null;
  /** Creates or updates one asset (admin). Absent hides the registry. */
  onSaveAsset?: ((draft: any) => Promise<any>) | null;
  onDeleteAsset?: ((id: string) => Promise<any>) | null;
}

/** The colour of an equipment block in the grid — one hue, apart from the court origins. */
const ASSET_BLOCK_COLOR = '#7c3aed';

/**
 * The TEXT shade of each slot-availability colour (#1144, WCAG 1.4.3).
 *
 * The slot card paints its dot and border in the availability colour, and its
 * availability line («Alle baner», «2 av 4 ledig», «Siste bane») used to be
 * painted in the same colour — 1.92:1 for yellow, 2.80 for orange and 3.30 for
 * green, as 10 px words on the white card. Each entry is the lightest shade of
 * the same OKLCH hue and chroma that reaches 4.5:1 on white (`shadeFor`, the
 * method of CONTRAST_REVIEW §9). Red already passes (4.83:1) and keeps its own.
 */
export const SLOT_AVAILABILITY_TEXT: Readonly<Record<string, string>> = {
  '#16a34a': '#03883a',
  '#eab308': '#94700b',
  '#f97316': '#bf5711',
  '#dc2626': '#dc2626',
};

/** Banebooking screen — day/slot/matrix calendar, smart-booking slot finder, admin tools, recurrence preview and the create/edit/payment modals; extracted verbatim from the monolith (legacy/App.jsx). */
export function BookingView({
  bookings = [],
  initialDate = null,
  totalCourts = 4,
  zones = [],
  config = null,
  bookingHoursSaved = null,
  bookingConfigRead = true,
  bookingZonesRead = true,
  bookingGuestPricesRead = true,
  bookingSaveFailed = false,
  onReloadConfig = null,
  serieContext = null,
  onUpdateZones,
  onUpdateConfig,
  guestPrices = null,
  onUpdateGuestPrices,
  onCourtStateChanged = null,
  onBack,
  onAddBooking: onAddBookingProp,
  onAddManyBookings,
  onDeleteManyBookings,
  onReplaceSeries,
  onUpdateBooking: onUpdateBookingProp,
  onDeleteBooking,
  staffRefund: staffRefundDoor = null,
  onOpenTournament = null,
  players = [],
  onPersistNewPlayer = null,
  onRegisterPlannedMatch = null,
  onCreateLinkedActivity = null,
  plannedMatches = [],
  assets = [],
  assetBookings = [],
  onAddAssetBooking = null,
  onUpdateAssetBooking = null,
  onDeleteAssetBooking = null,
  onSaveAsset = null,
  onDeleteAsset = null,
}: BookingViewProps) {
  const { t, formatDate } = useLocale();
  // «Avbestill og refunder» (#284) — null unless this session holds `payment.refund`.
  const staffRefund = staffRefundDoor?.allowed ? staffRefundDoor : null;
  /** The `booking_has_payment` refusal, pointing to the door this session has (or lacks). */
  const hasPaymentSentence = () =>
    t(staffRefund ? 'booking.view.alert.hasPayment' : 'booking.view.alert.hasPaymentNoRight');
  /**
   * A series edit that failed. A refusal for a booking it would have replaced
   * (#1374) carries its own sentence — nothing changed, and why — from
   * `useBookingActions`; anything else keeps the text it had.
   */
  const replaceFailedSentence = (r: { refusedBookings?: unknown; error?: unknown }) =>
    r.refusedBookings && typeof r.error === 'string'
      ? r.error
      : t('booking.view.alert.seriesUpdateFailed');
  // Ids for the dialogs' titles and the court-state note (#900). From
  // `useId`, never literal: a literal id is a duplicate the second time the
  // view renders (a11y-literal-ids.test.ts).
  const uid = safeId('booking-view', useId());
  const ids = {
    overrideTitle: `${uid}-override-title`,
    overrideBody: `${uid}-override-body`,
    clearAllTitle: `${uid}-clear-all-title`,
    clearAllBody: `${uid}-clear-all-body`,
    seriesTitle: `${uid}-series-title`,
    seriesLead: `${uid}-series-lead`,
    recurTitle: `${uid}-recur-title`,
    recurCounts: `${uid}-recur-counts`,
    courtState: (court: number) => `${uid}-court-state-${court}`,
    courtStateHint: (court: number) => `${uid}-court-state-hint-${court}`,
    courtStateError: (court: number) => `${uid}-court-state-error-${court}`,
  };
  // Weekday shorthands, DISPLAY only — the ISO date is the stored value and the
  // comparison key (docs/architecture/DOMAIN_RULES.md §4), never these labels.
  const weekdayCap = [
    t('booking.view.weekday.sun'),
    t('booking.view.weekday.mon'),
    t('booking.view.weekday.tue'),
    t('booking.view.weekday.wed'),
    t('booking.view.weekday.thu'),
    t('booking.view.weekday.fri'),
    t('booking.view.weekday.sat'),
  ];
  const weekdayLower = [
    t('booking.weekday.short.sun'),
    t('booking.weekday.short.mon'),
    t('booking.weekday.short.tue'),
    t('booking.weekday.short.wed'),
    t('booking.weekday.short.thu'),
    t('booking.weekday.short.fri'),
    t('booking.weekday.short.sat'),
  ];
  // Booking ids that carry a single match (local = planned match; via-aktiviteter
  // = the activity-linked court). Used to tag them as "Enkeltmatch" in the calendar.
  const singleMatchBookingIds = new Set((plannedMatches || []).map((p) => p.bookingId));
  const isSingleMatchBooking = (b) =>
    b?.source === 'activity_linked' || singleMatchBookingIds.has(b?.id);
  // Series reservation: discount + extended booking window + match→booking link.
  const serieDiscount = serieContext?.discountPct
    ? Math.max(0, Math.min(100, serieContext.discountPct))
    : 0;
  const applySerieDiscount = (total) =>
    serieDiscount ? Math.round((total || 0) * (1 - serieDiscount / 100)) : total || 0;
  // Effective config — merge the incoming config with defaults
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(config || {}) };
  const openTime = cfg.openTime;
  const closeTime = cfg.closeTime;
  // WHICH COURTS THIS CLUB HAS — the shared answer, so the slot list, the
  // modal, the member app and the write path cannot disagree about it.
  const effectiveCourts = resolveEffectiveCourts(cfg, totalCourts);
  const courtMeta = cfg.courtMeta || {};
  // Display name for a court: the nickname, else the default court label.
  const baneNavn = (c) =>
    courtDisplayName
      ? courtDisplayName(c, courtMeta)
      : courtMeta[c]?.nickname?.trim() || t('booking.view.courtName', { n: String(c) });
  // PHYSICAL COURT STATE (#271) — carried into the grid, NOT into `effectiveCourts`.
  //
  // `enabledCourts` (above) decides which courts the grid SHOWS; this decides
  // which of them are usable. They stay separate on purpose: a court that is
  // out of service is still offered and still drawn, with its bookings and its
  // state visible, because staff dealing with a closure need to see what is on
  // the court they are closing. Filtering it out of `effectiveCourts` would
  // collapse the two concepts and hide exactly what they came to look at.
  //
  // Nothing here decides whether a booking may be made. The server refuses a
  // broken court and decides who may override (#304); this only means the
  // operator finds out before they commit rather than after.
  const courtState = (c) => courtOutOfServiceState(c, courtMeta);
  // Local ISO date (YYYY-MM-DD) — NOT toISOString(), which yields UTC and can be
  // off by a day around midnight/time zones. Uses local components consistently.
  const toLocalIso = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const todayIso = toLocalIso(new Date());
  const [selectedDate, setSelectedDate] = useState(
    initialDate && initialDate >= todayIso ? initialDate : todayIso,
  );
  const [editingBooking, setEditingBooking] = useState(null);
  // When a series booking is clicked: ask whether to edit this occurrence
  // or the whole series before the editor opens. { booking } while the choice is shown.
  const [seriesChoice, setSeriesChoice] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  // editScope: 'single' | 'series' — set when the editor opens for a series.
  const [editScope, setEditScope] = useState('single');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addInitial, setAddInitial] = useState(null); // { court, startTime } from a click on an empty cell
  // Collision overview for recurring/multi-booking before creation.
  const [recurPreview, setRecurPreview] = useState(null); // { base, combos:[{iso,court,overlap,conflictWith}] }
  // View mode: 'slots' = aggregated free times (default/landing),
  // 'calendar' = per-court calendar across multiple days (rows=time, columns=days).
  const [viewMode, setViewMode] = useState('slots');
  const [showDatePicker, setShowDatePicker] = useState(false); // custom calendar popover
  // For the calendar view: how many days ahead we show, and the court filter.
  // DEVIATION (lint): these state pairs were declared-but-unused in the original
  // artifact (relics of a removed 'calendar' viewMode); `_`-prefixing the names satisfies
  // no-unused-vars without changing behaviour. Otherwise byte-identical.
  const [_calendarDays, _setCalendarDays] = useState(30);
  const [_calendarCourtFilter, _setCalendarCourtFilter] = useState(null); // null = all courts
  // Duration filter for the slot view: a SET of enabled durations
  // (on/off, multi-select). Persisted per user via Storage so the choice
  // is remembered next time. Default: 90 min on.
  const [selectedDurations, setSelectedDurations] = useState([90]);
  const durationsLoaded = useRef(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await Storage.get('booking_slot_durations_v1');
        const val = r?.value ? JSON.parse(r.value) : null;
        if (Array.isArray(val)) setSelectedDurations(val);
      } catch {
        /* ignore — use default */
      }
      durationsLoaded.current = true;
    })();
  }, []);
  // Save when the user changes the filter (after the init load is done).
  useEffect(() => {
    if (!durationsLoaded.current) return;
    Storage.set('booking_slot_durations_v1', JSON.stringify(selectedDurations)).catch(() => {});
  }, [selectedDurations]);
  // Toggle a duration on/off. Empty is allowed (makes it easy to reset and rebuild
  // the filter from scratch) — then no slots are shown until at least one is turned on again.
  const toggleDuration = (d) => {
    setSelectedDurations((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );
  };
  // Day-part grouping of the slot list (Morgen / Formiddag / Ettermiddag / Kveld).
  // Optional — some prefer one flat grid. Persisted like the duration filter.
  const [groupByDaypart, setGroupByDaypart] = useState(true);
  const groupingLoaded = useRef(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await Storage.get('booking_slot_grouping_v1');
        const val = r?.value ? JSON.parse(r.value) : null;
        if (typeof val === 'boolean') setGroupByDaypart(val);
      } catch {
        /* ignore — use default */
      }
      groupingLoaded.current = true;
    })();
  }, []);
  useEffect(() => {
    if (!groupingLoaded.current) return;
    Storage.set('booking_slot_grouping_v1', JSON.stringify(groupByDaypart)).catch(() => {});
  }, [groupByDaypart]);
  const [showZoneEditor, setShowZoneEditor] = useState(false);
  // EVERY document the price editor writes has been read (#1362): the zones,
  // the guest prices, and the venue setup for its default price. Until then the
  // editor is closed and its save refused, whatever changed.
  const pricesRead = bookingConfigRead && bookingZonesRead && bookingGuestPricesRead;
  // «Last inn på nytt» for unread booking settings (#1359, #1362).
  const [configReloading, setConfigReloading] = useState(false);
  const [configReloadFailed, setConfigReloadFailed] = useState(false);
  // Settings and help
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  // ── Equipment beside the courts (docs/domains/ASSET_BOOKING.md) ──
  const [showAssetEditor, setShowAssetEditor] = useState(false);
  const [assetBookingModal, setAssetBookingModal] = useState<{
    mode: 'create' | 'edit';
    initial: any;
  } | null>(null);
  // What this venue keeps, what is booked today, and which lanes to draw: the
  // active assets, plus an inactive one that still has a booking today — a
  // retired machine's reservations do not disappear from the day.
  const venueAssets = assets.filter((a) => a?.atThisVenue !== false);
  const bookableAssets = venueAssets.filter((a) => a.active !== false);
  const dayAssetBookings = assetBookingsForDay(assetBookings, selectedDate);
  const assetRows = venueAssets.filter(
    (a) => a.active !== false || dayAssetBookings.some((b) => b.assetId === a.id),
  );
  // WHAT THE CALENDAR SHOWS: everything, only the courts, or only the equipment
  // — and, for equipment, one kind at a time. A view preference, not a rule:
  // nothing is booked or refused differently because of it, so it lives in the
  // screen rather than in storage.
  const [laneFilter, setLaneFilter] = useState<'all' | 'courts' | 'assets'>('all');
  const [assetKindFilter, setAssetKindFilter] = useState<string | null>(null);
  const assetKinds = [...new Set(assetRows.map((a) => String(a.kind ?? 'other')))];
  // THE FILTER APPLIED is derived from the one chosen (#986). Its controls
  // render only while they are relevant — the group needs equipment lanes, the
  // kind chips two kinds — so a choice can outlive the control that would clear
  // it: a kind whose last lane was retired, or «only equipment» once the last
  // machine is unavailable. Such a choice counts as no filter, rather than
  // silently hiding lanes with nothing left on screen to undo it. The choice
  // itself is kept, and applies again when there is something to filter.
  const effectiveAssetKindFilter =
    assetKindFilter != null && assetKinds.includes(assetKindFilter) ? assetKindFilter : null;
  const effectiveLaneFilter = assetRows.length === 0 ? 'all' : laneFilter;
  const visibleCourts = effectiveLaneFilter === 'assets' ? [] : effectiveCourts;
  const visibleAssetRows =
    effectiveLaneFilter === 'courts'
      ? []
      : assetRows.filter(
          (a) =>
            effectiveAssetKindFilter == null ||
            String(a.kind ?? 'other') === effectiveAssetKindFilter,
        );
  // EQUIPMENT CHOSEN WITH A COURT BOOKING is booked right after the court is,
  // attached to it. Two commands rather than one on purpose: the court
  // booking's own command is untouched, and a machine that turns out to be
  // taken in between costs the operator a message, not the court.
  const attachAssets = async (
    bookingId: string | null | undefined,
    data: any,
    assetIds: string[] | undefined,
  ) => {
    if (!bookingId || !onAddAssetBooking || !Array.isArray(assetIds) || assetIds.length === 0)
      return;
    const failed: string[] = [];
    for (const assetId of assetIds) {
      const r = await onAddAssetBooking({
        assetId,
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        bookingId: String(bookingId),
        bookedBy: data.bookedBy ?? null,
        adminOverride: adminMode,
      });
      if (!r?.success) failed.push(assetBookingErrorMessage(t, r ?? {}));
    }
    if (failed.length > 0)
      alert(t('booking.view.alert.assetFailed', { error: failed.join(' · ') }));
  };
  const [showHelp, setShowHelp] = useState(false);
  // Admin mode: skips the payment flow and lets the admin set paymentStatus
  // directly. Stored locally in the session (not persisted) — the admin must enable
  // it each time to prevent it being left on by accident.
  // ── THE OVERRIDE RETRY (#271) ──────────────────────────────────────
  //
  // The server refuses a booking on a court that is out of service and tells
  // the caller whether THEY may proceed anyway. Until now the refusal said «Du
  // kan overstyre dette» and offered no way to — a promise with no button.
  //
  // ONE prompt, owned here, rather than a retry at each of the eight places
  // that create or move a booking. `onAddBooking`/`onUpdateBooking` below SHADOW
  // the props of the same name: every existing call site is unchanged and
  // keeps whatever it does with `r.success`, because the retry resolves the
  // original promise with the result of the second attempt.
  //
  // `overridable` is the SERVER's answer about this caller. The prompt is never
  // shown to someone who would only be refused again, and the browser does not
  // work out who is entitled — it asks and is told.
  const [overridePrompt, setOverridePrompt] = useState(null);
  // { refusal, retry: () => Promise<any>, resolve, refused }

  /**
   * Wraps a write so an overridable out-of-service refusal becomes a question
   * instead of a dead end.
   *
   * `retry` re-runs the SAME intent with the flag added — not a rebuilt one, so
   * nothing can drift between what was refused and what is retried.
   */
  const withOverridePrompt =
    (run) =>
    async (...args) => {
      const opts = args[args.length - 1];
      const wantsPrompt = !(opts && typeof opts === 'object' && opts.promptOverride === false);
      const refused = await run(args, false);
      const refusal = refused?.refusal;
      if (
        refused?.success ||
        !wantsPrompt ||
        !refusal ||
        refusal.error !== 'court_out_of_service' ||
        !refusal.overridable
      ) {
        return refused;
      }
      return new Promise((resolve) => {
        setOverridePrompt({
          refusal,
          refused,
          resolve,
          retry: () => run(args, true),
        });
      });
    };

  const onAddBooking = withOverridePrompt((args, override) => {
    const [data, opts = {}] = args;
    return onAddBookingProp(data, override ? { ...opts, overrideCourtOutOfService: true } : opts);
  });
  const onUpdateBooking = withOverridePrompt((args, override) => {
    const [id, patch, opts = {}] = args;
    return onUpdateBookingProp(
      id,
      patch,
      override ? { ...opts, overrideCourtOutOfService: true } : opts,
    );
  });

  const [adminMode, setAdminMode] = useState(false);
  // CLOSING / REOPENING A COURT (#271) — admin tools, in the grid.
  //
  // Not in the settings editor: the net comes down in the middle of a day, and
  // the person who finds it is at the desk looking at the calendar, not in a
  // configuration dialog behind an unrelated «Lagre» that would rewrite the
  // club's opening hours at the same time.
  //
  // `courtStateEdit` is the open panel: { court, reason }. A panel rather than
  // window.confirm/prompt, which do nothing in an iframe (DOMAIN_RULES §9).
  const [courtStateEdit, setCourtStateEdit] = useState(null); // { court, reason }
  // THE REASON FIELD TAKES FOCUS WHEN ITS PANEL OPENS — by an effect on the
  // admin's own «Steng»/«Åpne» click rather than `autoFocus` (#900): the same
  // keyboard result, and never a focus move nobody asked for. Only one panel is
  // open at a time, so one ref serves every lane.
  const courtStateInputRef = useRef<HTMLInputElement | null>(null);
  const courtStateCourt = courtStateEdit?.court;
  useEffect(() => {
    if (courtStateCourt != null) courtStateInputRef.current?.focus();
  }, [courtStateCourt]);
  const [courtStateBusy, setCourtStateBusy] = useState(false);
  const [courtStateError, setCourtStateError] = useState(null);
  const applyCourtState = async (court, reason) => {
    setCourtStateBusy(true);
    setCourtStateError(null);
    try {
      const res =
        reason === null
          ? await clearCourtOutOfService(court)
          : await setCourtOutOfService(court, reason);
      // The command already wrote it. This only refreshes the copy on screen —
      // see onCourtStateChanged on why it does not go through the config save.
      onCourtStateChanged?.(res.config);
      setCourtStateEdit(null);
    } catch (e) {
      setCourtStateError(errMsg(e) || t('booking.view.courtStateError'));
    } finally {
      setCourtStateBusy(false);
    }
  };
  // Pending payment data: when the user has filled out the booking modal and clicked
  // «Til betaling», we hold the booking data here while BookingPaymentModal is shown.
  // When payment succeeds → we commit the booking via onAddBooking; on cancel
  // we just clear this state and let the user try again.
  const [pendingPayment, setPendingPayment] = useState(null);

  const toMin = toMinHHMM;
  const fromMin = fromMinHHMM;

  const dayBookings = useMemo(
    () => getBookingsForDay(bookings, selectedDate),
    [bookings, selectedDate],
  );

  // Calendar geometry
  const PX_PER_MIN = 1.2; // 1.2 px per min → 1 hour = 72 px
  // DEVIATION (lint): declared-but-unused in the original artifact; `_`-prefixed to
  // satisfy no-unused-vars without changing behaviour. Otherwise byte-identical.
  const _HOUR_PX = 60 * PX_PER_MIN;
  const startMin = toMin(openTime);
  let endMin = toMin(closeTime);
  // Over midnight: a close time at/before opening is interpreted as the next day (+24h).
  // Open around the clock (00:00–00:00) → 1440.
  if (endMin <= startMin) endMin += 1440;
  const hours = [];
  for (let h = Math.floor(startMin / 60); h <= Math.floor(endMin / 60); h++) {
    hours.push(h); // can be > 23 when over midnight; shown mod 24 in the UI
  }
  const totalMin = endMin - startMin;
  // "Now" position in the matrix — only relevant when the selected day is today and the
  // current time is within opening hours. nowMin = minutes since midnight. For over-midnight
  // (e.g. 05–01) the early morning (00–01) is also "within" — add 1440.
  const nowDate = new Date();
  const isViewingToday = selectedDate === todayIso;
  const rawNowMin = nowDate.getHours() * 60 + nowDate.getMinutes();
  // If we are in the over-midnight period (after midnight but before closing), adjust.
  const nowMin = endMin > 1440 && rawNowMin < endMin - 1440 ? rawNowMin + 1440 : rawNowMin;
  const nowWithinOpen = isViewingToday && nowMin >= startMin && nowMin <= endMin;
  const matrixScrollRef = useRef(null);
  // Hover in the matrix: shows which durations fit from an empty point.
  const [hoverSlot, setHoverSlot] = useState(null); // { court, startMin, durations:[], leftPx }
  // WHAT THE CLUB OFFERS — one expression, used by the matrix, the slot list
  // and the slot list's filter chips.
  //
  // The chips used to be a hard-coded [60, 90, 120] while the slot computation
  // intersected with this, which broke in both directions: a club offering 180
  // (or a custom duration) had no chip to reach it, and a club NOT offering 120
  // still showed one — turning it on produced nothing, and turning ONLY it on
  // produced «choose a duration» while a chip sat lit. The settings dialog is
  // where a club says what it offers; this list is where a customer sees it,
  // and they have to be the same answer.
  const allowedDurs =
    cfg.allowedDurations && cfg.allowedDurations.length > 0 ? cfg.allowedDurations : [60, 90, 120];

  // THE CENTRE'S HOURS, AS A VALIDATION OPTION — and omitted in admin mode.
  //
  // Mirrors the server's `bookingValidationOptions` exactly: the ordinary path
  // asks for the rule by passing the window, and the override asks for it to be
  // skipped by leaving it out. Two expressions of one decision, which is the
  // most the client can do — the SERVER is what actually refuses.
  //
  // Without this the matrix offered what it could not deliver: at 18:30 against
  // a 19:00 close the hover chip read «60m / 90m / 120m» and the green
  // candidate ran off the end of the axis. Now the engine knows, the chip stops
  // lying; an operator in admin mode still sees them, deliberately.
  //
  // Both times or neither — a half-configured window is not a window, and the
  // engine already treats it that way.
  const hoursOpts =
    adminMode || typeof openTime !== 'string' || typeof closeTime !== 'string'
      ? {}
      : { openTime, closeTime };

  // Where the «closed» band starts in the matrix, or null when there is nothing
  // to mark. A centre open round the clock has no closing edge to draw, and
  // drawing one at the far right would invent a boundary it does not have.
  const closedBandLeft =
    typeof closeTime === 'string' && totalMin < 1440 ? 80 + totalMin * PX_PER_MIN : null;

  // A HOLE IN THE CALENDAR IS BOOKABLE AT ITS OWN LENGTH.
  //
  // Owner, 2026-09-28: «hvis det er booket til feks 1830 og sentret stenger
  // 1900 — da må det være lov å booke 1830-19 selv om det ikke er lov å booke
  // 30 minutter.»
  //
  // The engine already permits it: `validateBooking` checks the gaps a booking
  // LEAVES BEHIND, never the candidate's own length, so `allowedDurations`
  // reaches it only through `gapFillableByDurations`. Pinned in
  // `booking-opening-hours-characterization.test.ts`. What stopped an operator
  // was this list — the club's durations and nothing else — so the half hour
  // before closing could not be sold at all.
  //
  // SMALLER THAN THE LARGEST ALLOWED DURATION, which is what makes it a hole
  // rather than the rest of the day: without that, hovering 12:00 against a
  // 19:00 close would offer a seven-hour booking as «the remainder».
  //
  // `minBookingMin` and the smart rule keep reading the CLUB'S list, not this
  // one. The dead-gap limit is «how short a gap may we leave», and letting it
  // fall to 30 because one hole is 30 long would loosen the gap rule everywhere.
  const durationsToTry = (court: number, from: number): number[] => {
    const list = [...allowedDurs];
    const longest = Math.max(...allowedDurs);
    const nextStart = dayBookings
      .filter((b) => b.court === court && toMin(b.startTime) > from)
      .reduce((lowest, b) => Math.min(lowest, toMin(b.startTime)), Infinity);
    // Admin mode is not bounded by closing, for the same reason it skips the
    // opening-hours rule entirely.
    const boundary = Math.min(nextStart, adminMode ? Infinity : endMin);
    const remainder = boundary - from;
    if (
      Number.isFinite(remainder) &&
      remainder > 0 &&
      remainder < longest &&
      remainder % 30 === 0 &&
      !list.includes(remainder)
    ) {
      list.push(remainder);
    }
    return list.sort((a, b) => a - b);
  };
  const didMatrixScroll = useRef(false);
  // Auto-scroll to "now" only the FIRST time the matrix opens (on today). When the
  // day is switched the scroll position stays where it is — it does not jump.
  useEffect(() => {
    if (viewMode !== 'matrix') {
      didMatrixScroll.current = false;
      return;
    }
    if (didMatrixScroll.current) return; // already scrolled — leave the position alone
    if (nowWithinOpen && matrixScrollRef.current) {
      const x = (nowMin - startMin) * PX_PER_MIN - 120; // a little margin on the left
      matrixScrollRef.current.scrollLeft = Math.max(0, x);
      didMatrixScroll.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, nowWithinOpen]);

  // Click on an empty cell → open the add modal with court+time pre-filled
  const handleEmptyClick = (court, clickX, rect) => {
    // Direct booking from the matrix can be turned off in the settings. Then the
    // matrix is view-only, and the admin/customer must book via the slot view.
    // Admin mode is always allowed (they control the calendar directly).
    if (cfg.allowDirectBooking === false && !adminMode) return;
    const minOffset = (clickX - rect.left) / PX_PER_MIN;
    const clickedMin = startMin + minOffset;
    // Suggest the start of the hour the user clicks in (XX:00) by default. But if
    // that half-hour is taken (e.g. an existing booking covers XX:00–XX:30),
    // we suggest the half-hour (XX:30) instead — so the suggestion hits the
    // actually free start point where the user clicked.
    const hourStart = Math.floor(clickedMin / 60) * 60;
    const halfStart = hourStart + 30;
    // Check whether a given half-hour start is free on this court (covered by a booking?)
    const startTaken = (m) =>
      dayBookings.some((b) => b.court === court && toMin(b.startTime) <= m && toMin(b.endTime) > m);
    // Choose the hour start if free; otherwise the half-hour if it is free and the user
    // actually clicked in the second half; otherwise keep the hour start (the modal then
    // shows that the court is taken and lets the user adjust).
    let candidateStart = hourStart;
    if (startTaken(hourStart) && !startTaken(halfStart)) {
      candidateStart = halfStart;
    }
    candidateStart = Math.max(startMin, Math.min(endMin - 90, candidateStart));
    // Choose the longest duration that is ACTUALLY bookable here (full smart validation, =
    // green square). Falls back to the longest physically possible if none is
    // green (the modal then shows why / suggests another court).
    const minBM = Math.min(...allowedDurs);
    const smartOn = cfg.smartAllocation !== false;
    const smartOpts = {
      bufferMin: cfg.bufferMin,
      minBookingMin: minBM,
      smartAllocation: true,
      totalCourts: effectiveCourts.length,
      enabledCourts: effectiveCourts,
      allowedDurations: allowedDurs,
      ...hoursOpts,
    };
    const phys = [],
      full = [];
    for (const d of durationsToTry(court, candidateStart)) {
      const cand = {
        court,
        date: selectedDate,
        startTime: fromMin(candidateStart),
        endTime: fromMin(candidateStart + d),
      };
      if (
        !validateBooking(cand, dayBookings, {
          bufferMin: cfg.bufferMin,
          minBookingMin: minBM,
          smartAllocation: false,
          ...hoursOpts,
        }).ok
      )
        continue;
      phys.push(d);
      if (!smartOn || validateBooking(cand, dayBookings, smartOpts).ok) full.push(d);
    }
    const bestDur = full.length ? Math.max(...full) : phys.length ? Math.max(...phys) : null;
    setAddInitial({
      court,
      startTime: fromMin(candidateStart),
      date: selectedDate,
      ...(bestDur ? { durationMin: bestDur } : {}),
    });
    setShowAddModal(true);
  };

  // Hover on an empty cell → show which durations fit from this
  // (half-hour-snapped) start point. GREEN = actually bookable here. RED = would
  // create an indivisible gap here (but may be allowed on another court) — only when smart
  // booking is on. Taken points show no tooltip.
  const handleEmptyHover = (court, clickX, rect) => {
    if (cfg.allowDirectBooking === false && !adminMode) {
      setHoverSlot(null);
      return;
    }
    const minOffset = (clickX - rect.left) / PX_PER_MIN;
    let snapped = Math.round((startMin + minOffset) / 30) * 30;
    snapped = Math.max(startMin, Math.min(endMin - 30, snapped));
    const taken = dayBookings.some(
      (b) => b.court === court && toMin(b.startTime) <= snapped && toMin(b.endTime) > snapped,
    );
    if (taken) {
      setHoverSlot((prev) => (prev ? null : prev));
      return;
    }
    const minBM = Math.min(...allowedDurs);
    const smartOn = cfg.smartAllocation !== false;
    const smartOpts = {
      bufferMin: cfg.bufferMin,
      minBookingMin: minBM,
      smartAllocation: true,
      totalCourts: effectiveCourts.length,
      enabledCourts: effectiveCourts,
      allowedDurations: allowedDurs,
      ...hoursOpts,
    };
    const green = [],
      red = [];
    for (const d of durationsToTry(court, snapped)) {
      const cand = {
        court,
        date: selectedDate,
        startTime: fromMin(snapped),
        endTime: fromMin(snapped + d),
      };
      const physOk = validateBooking(cand, dayBookings, {
        bufferMin: cfg.bufferMin,
        minBookingMin: minBM,
        smartAllocation: false,
        ...hoursOpts,
      }).ok;
      if (!physOk) continue;
      if (!smartOn) {
        green.push(d);
        continue;
      }
      const fullOk = validateBooking(cand, dayBookings, smartOpts).ok;
      if (fullOk) green.push(d);
      else red.push(d);
    }
    // Find a better court: when no duration is GREEN here (either because it creates
    // an indivisible gap, or because nothing is physically possible here), look for
    // another court where the longest allowed duration is actually permitted at this time.
    let suggestCourt = null,
      suggestDur = null;
    if (smartOn && green.length === 0) {
      for (const c of effectiveCourts) {
        if (c === court) continue;
        for (const d of durationsToTry(c, snapped).sort((a, b) => b - a)) {
          const cand = {
            court: c,
            date: selectedDate,
            startTime: fromMin(snapped),
            endTime: fromMin(snapped + d),
          };
          if (validateBooking(cand, dayBookings, smartOpts).ok) {
            if (suggestDur == null || d > suggestDur) {
              suggestCourt = c;
              suggestDur = d;
            }
            break; // longest for this court
          }
        }
      }
    }
    // WHAT ADMIN MODE IS BUYING, said out loud.
    //
    // The owner asked for the override to survive AND for the operator to be
    // told what is being overridden. In admin mode `hoursOpts` is empty, so an
    // out-of-hours duration comes back GREEN and looks like any other — which
    // is precisely the state that produced the report. So the check is run
    // again here, with the hours, purely to name what the green is standing on.
    //
    // Only in admin mode: for everyone else these durations were filtered out
    // above and there is nothing to warn about.
    const outsideHours =
      adminMode && typeof openTime === 'string' && typeof closeTime === 'string'
        ? green.filter(
            (d) =>
              !validateBooking(
                {
                  court,
                  date: selectedDate,
                  startTime: fromMin(snapped),
                  endTime: fromMin(snapped + d),
                },
                dayBookings,
                {
                  bufferMin: cfg.bufferMin,
                  minBookingMin: minBM,
                  smartAllocation: false,
                  openTime,
                  closeTime,
                },
              ).ok,
          )
        : [];
    // ADMIN MODE OVERRIDES SMART BOOKING TOO — the owner asked for both.
    //
    // `red` is «legal here, but another court packs the calendar better», and
    // the matrix used to simply refuse it whoever you were. The server has
    // always allowed it under `adminOverride` (`smartAllocation: false`), so
    // the view was the stricter of the two. It is offered now, and the list is
    // KEPT rather than discarded — it is exactly what the warning names.
    const againstSmart = adminMode ? [...red] : [];
    if (adminMode && red.length > 0) {
      green.push(...red);
      green.sort((a, b) => a - b);
      red.length = 0;
    }
    setHoverSlot({
      court,
      startMin: snapped,
      leftPx: (snapped - startMin) * PX_PER_MIN,
      green,
      red,
      outsideHours,
      againstSmart,
      suggestCourt,
      suggestDur,
    });
  };

  // Date nav
  // DEVIATION (lint): declared-but-unused in the original artifact; `_`-prefixed to
  // satisfy no-unused-vars without changing behaviour. Otherwise byte-identical.
  const _shiftDate = (deltaDays) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + deltaDays);
    setSelectedDate(toLocalIso(d));
  };

  // Compute the availability ratio for a given day: the share of all (court × half-hour slot)
  // that are free. Used for the color indicator on the day quick buttons.

  // Number of days regular users can book ahead (incl. today). Admin overrides.
  // Series reservation: use the series' extended window if set.
  // The series context sets its own horizon; otherwise the club's window,
  // through the shared resolver so this grid, the member app and the server
  // cannot drift apart (#234).
  const bookingWindowDays =
    serieContext?.windowDays != null ? serieContext.windowDays : resolveBookingWindowDays(cfg);
  // How many day quick buttons we show. Admin sees more than the window, so
  // they can book outside the normal window; regular users see a bit beyond the window
  // (with a padlock on the locked ones) so they understand it opens later.
  const quickDayCount = adminMode ? 30 : Math.min(bookingWindowDays + 7, 30);
  const quickDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < quickDayCount; i++) {
      const d = new Date(todayIso + 'T00:00:00');
      d.setDate(d.getDate() + i);
      days.push(toLocalIso(d));
    }
    return days;
  }, [todayIso, quickDayCount]);
  // A day is locked for regular users if it is further ahead than the window.
  const isDateLocked = (iso) => {
    if (adminMode) return false;
    const d = new Date(iso + 'T00:00:00'),
      t = new Date(todayIso + 'T00:00:00');
    const diff = Math.round((d.getTime() - t.getTime()) / 86400000);
    return diff < 0 || diff >= bookingWindowDays;
  };

  const dayAvailability = useMemo(() => {
    // Availability is computed for three segments of the day — early, midday, evening — so
    // that a day that is full in the evening but empty during the daytime does not look "green".
    // The segments split the opening hours into three equal parts. Handles over midnight
    // (closing the next day) by adding 24h to closeMin.
    const oM = toMin(cfg.availabilityFromTime || openTime);
    let cM = toMin(cfg.availabilityToTime || closeTime);
    if (cM <= oM) cM += 1440;
    const span = cM - oM;
    const segBounds = [oM, oM + span / 3, oM + (2 * span) / 3, cM];
    const map = {};
    const now = new Date();
    const nowIso = toLocalIso(now);
    const nowMin = now.getHours() * 60 + now.getMinutes();
    quickDays.forEach((iso, dayIdx) => {
      // Include bookings on this day + (for over midnight) early the next day.
      const nextIso = quickDays[dayIdx + 1];
      const relevant = bookings.filter(
        (b) =>
          effectiveCourts.includes(b.court) &&
          (b.date === iso || (cM > 1440 && b.date === nextIso)),
      );
      // For today: past hours should not count as "free capacity" — they are
      // over. We cut the segments at the current time.
      const isToday = iso === nowIso;
      const segFree = segBounds.slice(0, 3).map((segStartRaw, i) => {
        let segStart = segStartRaw;
        const segEnd = segBounds[i + 1];
        // Skip / shrink segments that lie in the past today.
        if (isToday) {
          if (segEnd <= nowMin) return null; // fully past
          if (segStart < nowMin) segStart = nowMin; // partly past — start from now
        }
        const slotsPerCourt = Math.max(1, Math.round((segEnd - segStart) / 30));
        const cap = effectiveCourts.length * slotsPerCourt;
        let busy = 0;
        relevant.forEach((b) => {
          // Normalize booking time to the same axis (next day = +1440)
          let bs = toMin(b.startTime),
            be = toMin(b.endTime);
          if (b.date === nextIso) {
            bs += 1440;
            be += 1440;
          } else if (be <= bs) be += 1440; // booking that crosses midnight
          const s = Math.max(bs, segStart),
            e = Math.min(be, segEnd);
          if (e > s) busy += Math.round((e - s) / 30);
        });
        return cap > 0 ? Math.max(0, (cap - busy) / cap) : 1;
      });
      map[iso] = segFree; // [early, midday, evening] — null = past segment
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookings,
    quickDays,
    openTime,
    closeTime,
    effectiveCourts,
    cfg.availabilityFromTime,
    cfg.availabilityToTime,
  ]);

  // Generate demo reservations: each button press fills about 20% of the remaining
  // free capacity for the selected day. Main idea: find free time windows per court,
  // and try to place ~20% of these as new bookings. This way the user can
  // click multiple times and watch the calendar gradually fill up.
  //
  // Realistic bookings: weighted toward 90 min (a typical padel hour), respects
  // the buffer rule (the engine rejects illegal attempts, we only try legal
  // placements by building on existing booking ends and the opening hours).
  const generateDemoBookings = async () => {
    const demoNames = DEMO_BOOKER_NAMES;
    // Follow the rules from config: allowed durations, gap (buffer), step, opening hours.
    // A copy, because the generator builds a weighted list from it below.
    const allowed = [...allowedDurs];
    const minDur = Math.min(...allowed);
    // Weight a bit toward the middle duration for a natural distribution.
    const durations = [...allowed, ...allowed.slice(1, -1)];
    const stepMin = cfg.stepMin || 30;
    const gapMin = cfg.bufferMin || 0; // smallest allowed gap to a neighboring booking

    const openMin = toMinHHMM(openTime);
    let closeMin = toMinHHMM(closeTime);
    // Over midnight / round the clock: a close time at/before opening = next day.
    if (closeMin <= openMin) closeMin += 1440;
    const dayLengthMin = closeMin - openMin;

    // We maintain a LOCAL list of bookings for the selected date. Each time we
    // successfully add a booking, we update this list and rebuild the
    // candidate windows from it. This avoids a race condition where subsequent
    // candidates overlap with a booking we just added.
    const dayBookings = bookings
      .filter((b) => b.date === selectedDate)
      .map((b) => ({
        court: b.court,
        startTime: b.startTime,
        endTime: b.endTime,
      }));

    const totalCapacityMin = effectiveCourts.length * dayLengthMin;
    const initialUsedMin = dayBookings.reduce(
      (sum, b) => sum + (toMinHHMM(b.endTime) - toMinHHMM(b.startTime)),
      0,
    );
    const remainingMin = Math.max(0, totalCapacityMin - initialUsedMin);
    // Target: about 20% of total capacity (or 50% of the remaining, whichever is lower)
    const targetMin = Math.min(totalCapacityMin * 0.2, remainingMin * 0.5);

    // Helper: given a court and a local list, find free windows on the day.
    // Returns [{ startMin, maxLenMin, mustEndAt }]
    const findVacanciesForCourt = (court, localDay) => {
      const courtBookings = localDay
        .filter((b) => b.court === court)
        .map((b) => {
          let s = toMinHHMM(b.startTime);
          let e = toMinHHMM(b.endTime);
          // Over midnight: normalize to the same axis as openMin/closeMin.
          if (closeMin > 1440 && s < openMin) {
            s += 1440;
            e += 1440;
          } else if (e < s) e += 1440;
          return { s, e };
        })
        .sort((a, b) => a.s - b.s);
      const out = [];
      let cursor = openMin;
      for (const b of courtBookings) {
        if (b.s > cursor) {
          for (let t = cursor; t < b.s; t += stepMin) {
            const maxLen = b.s - t;
            if (maxLen >= minDur)
              out.push({ court, startMin: t, maxLenMin: maxLen, mustEndAt: b.s });
          }
        }
        cursor = Math.max(cursor, b.e);
      }
      if (cursor < closeMin) {
        for (let t = cursor; t + minDur <= closeMin; t += stepMin) {
          const maxLen = closeMin - t;
          if (maxLen >= minDur)
            out.push({ court, startMin: t, maxLenMin: maxLen, mustEndAt: null });
        }
      }
      return out;
    };

    let placedMin = 0;
    let createdCount = 0;
    let skippedCount = 0;
    let safetyLoopBudget = 200; // don't run forever if something is off

    while (placedMin < targetMin && safetyLoopBudget-- > 0) {
      // Rebuild the candidate list based on the UPDATED local booking list
      const allCandidates = [];
      for (const court of effectiveCourts) {
        allCandidates.push(...findVacanciesForCourt(court, dayBookings));
      }
      if (allCandidates.length === 0) break;

      // Pick a random candidate
      const slot = allCandidates[Math.floor(Math.random() * allCandidates.length)];
      const validDurations = durations.filter((d) => d <= slot.maxLenMin);
      if (validDurations.length === 0) continue;
      // If we pack against an existing booking, we must either fill it completely or
      // leave a legal gap (>= the gap rule). A gap smaller than gapMin
      // would be rejected by validateBooking as a "dead gap".
      let chosen = null;
      if (slot.mustEndAt != null) {
        const lawful = validDurations.filter((d) => {
          const gap = slot.maxLenMin - d;
          return gap === 0 || gap >= gapMin;
        });
        if (lawful.length === 0) continue;
        chosen = lawful[Math.floor(Math.random() * lawful.length)];
      } else {
        chosen = validDurations[Math.floor(Math.random() * validDurations.length)];
      }
      const startTime = fromMinHHMM(slot.startMin);
      const endTime = fromMinHHMM(slot.startMin + chosen);
      const name = demoNames[Math.floor(Math.random() * demoNames.length)];
      // No saved zone, no price (#1312): such a demo booking is free.
      const price = calcCourtBookingPrice(zones, selectedDate, startTime, endTime, cfg);
      const r = Math.random();
      const status = !price ? 'free' : r < 0.6 ? 'paid' : r < 0.85 ? 'pending' : 'free';
      // Varied origin so the color codes show up in the demo: mostly users, some admin.
      const sr = Math.random();
      const source = sr < 0.75 ? 'user' : sr < 0.92 ? 'admin' : 'tournament';
      const result = await onAddBooking({
        court: slot.court,
        date: selectedDate,
        startTime,
        durationMin: chosen,
        bookedBy: { name, phone: '', email: '' },
        notes: '(demo)',
        paymentStatus: status,
        priceNok: status === 'free' || !price ? 0 : price.total,
        source,
      });
      if (result.success) {
        createdCount++;
        placedMin += chosen;
        // Update the local list so the next iteration sees the new booking
        dayBookings.push({ court: slot.court, startTime, endTime });
      } else {
        skippedCount++;
      }
    }

    if (createdCount === 0) {
      alert(t('booking.view.alert.demoFull'));
    } else {
      const finalUsedMin = initialUsedMin + placedMin;
      const newFillPct = Math.round((finalUsedMin / totalCapacityMin) * 100);
      console.log(
        `[Padel24] Demo: +${createdCount} bookings (${placedMin} min), fill now ~${newFillPct}%. Rejected: ${skippedCount}.`,
      );
    }
  };

  // Delete all demo reservations (identified by notes === '(demo)') for the selected date.
  // Keep the real bookings the admin has added.
  // Clear the WHOLE calendar — deletes all bookings (not just demo). Batch.
  const clearAllBookings = async () => {
    const ids = bookings.map((b) => b.id);
    if (ids.length === 0) {
      setConfirmClearAll(false);
      return;
    }
    if (onDeleteManyBookings) await onDeleteManyBookings(ids);
    else for (const id of ids) await onDeleteBooking(id);
    setConfirmClearAll(false);
  };

  const formatDateLabel = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return formatDate(d, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 slide-up">
      <button
        onClick={onBack}
        className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-3 flex items-center gap-1"
      >
        <ArrowLeft size={12} />{' '}
        {t(serieContext ? 'booking.view.backToProgram' : 'booking.view.home')}
      </button>

      {serieContext && (
        <div
          className="rounded-xl p-4 mb-4"
          style={{ background: '#f7fee7', border: '1px solid #84cc16' }}
        >
          <div
            className="font-mono text-[10px] uppercase tracking-wider mb-1"
            style={{ color: '#3f6212' }}
          >
            {t('booking.view.serie.label')}
          </div>
          <div className="font-semibold text-ink-1 text-sm">{serieContext.title}</div>
          <div className="text-[12px] text-ink-2 mt-1">
            {serieDiscount > 0 && (
              <span className="font-semibold" style={{ color: '#3f6212' }}>
                {t('booking.view.serie.discount', { pct: String(serieDiscount) })}
              </span>
            )}
            {serieDiscount > 0 && ' · '}
            {t('booking.view.serie.window', { days: String(bookingWindowDays) })}
          </div>
        </div>
      )}

      {!pricesRead && (
        // THE BOOKING SETTINGS COULD NOT BE READ — the venue setup (#1359), the
        // price zones or the guest prices (#1362). The editors that write them
        // are closed and every save of them is refused until a read answers, so
        // nothing goes out over settings this screen never saw. The way out is a
        // re-read. When only the prices failed, the settings dialog stays open
        // (it writes only the venue setup, which was read), and the text says
        // that it is the prices that cannot be changed.
        <div
          role="alert"
          className="rounded-2xl p-4 mb-4 border border-red-300 bg-red-50 text-red-900 flex items-start justify-between gap-3 flex-wrap"
        >
          <div className="min-w-0">
            {/* After a FAILED SAVE (#1370) the document is unread for a
                different reason, and the banner says that one. */}
            <div className="font-semibold text-sm">
              {bookingSaveFailed
                ? t('booking.view.configStale.title')
                : t('booking.view.configUnread.title')}
            </div>
            <p className="text-[13px] mt-1">
              {bookingSaveFailed
                ? t('booking.view.configStale.body')
                : bookingConfigRead
                  ? t('booking.view.configUnread.pricesBody')
                  : t('booking.view.configUnread.body')}
            </p>
            {configReloadFailed && (
              <p className="text-[13px] mt-1 font-semibold">
                {t('booking.view.configUnread.reloadFailed')}
              </p>
            )}
          </div>
          {onReloadConfig && (
            <button
              type="button"
              disabled={configReloading}
              onClick={async () => {
                setConfigReloading(true);
                setConfigReloadFailed(false);
                try {
                  const r = await onReloadConfig();
                  if (!r.success) setConfigReloadFailed(true);
                } finally {
                  setConfigReloading(false);
                }
              }}
              className="text-sm px-4 py-2 rounded-full bg-red-900 text-red-50 font-semibold whitespace-nowrap disabled:opacity-60"
            >
              {configReloading
                ? t('booking.view.configUnread.reloading')
                : t('booking.view.configUnread.reload')}
            </button>
          )}
        </div>
      )}

      {bookingHoursSaved === false && (
        // NOT OPEN YET (#1192). Said before anyone tries, with the way out: the
        // settings editor, where saving the hours is what opens booking.
        <div
          role="status"
          className="rounded-2xl p-4 mb-4 border border-amber-300 bg-amber-50 text-amber-900 flex items-start justify-between gap-3 flex-wrap"
        >
          <div className="min-w-0">
            <div className="font-semibold text-sm">{t('booking.view.hoursNotSet.title')}</div>
            <p className="text-[13px] mt-1">{t('booking.view.hoursNotSet.body')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowConfigEditor(true)}
            className="text-sm px-4 py-2 rounded-full bg-amber-900 text-amber-50 font-semibold whitespace-nowrap"
          >
            {t('booking.view.hoursNotSet.action')}
          </button>
        </div>
      )}

      <div
        className="rounded-2xl p-5 md:p-6 mb-5 text-white shadow-lg"
        style={{ background: 'linear-gradient(135deg, #047857 0%, #10b981 50%, #14b8a6 100%)' }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-1">
              {t('booking.view.eyebrow')}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-white leading-tight">
              {t('booking.view.title')}
            </h2>
            <p className="text-white/80 text-sm mt-1.5">{t('booking.view.lead')}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* Hovedhandling + admin-modus */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAdminMode(!adminMode)}
                title={t('booking.view.adminTitle')}
                className={`text-xs px-3 py-2 rounded-full font-semibold transition-all whitespace-nowrap border ${
                  adminMode
                    ? 'bg-amber-300 text-amber-900 border-amber-300 shadow'
                    : 'bg-white/15 text-white border-white/30 hover:bg-white/25'
                }`}
              >
                {t(adminMode ? 'booking.view.adminOn' : 'booking.view.adminOff')}
              </button>
              <button
                onClick={() => {
                  setAddInitial(null);
                  setShowAddModal(true);
                }}
                className="text-sm px-4 py-2 rounded-full bg-white text-emerald-700 font-semibold shadow hover:shadow-md transition-all whitespace-nowrap"
              >
                {t('booking.view.newBooking')}
              </button>
              {bookableAssets.length > 0 && onAddAssetBooking && (
                <button
                  onClick={() => setAssetBookingModal({ mode: 'create', initial: null })}
                  className="text-sm px-4 py-2 rounded-full bg-white/15 text-white border border-white/30 font-semibold hover:bg-white/25 transition-all whitespace-nowrap"
                >
                  {t('booking.view.newAssetBooking')}
                </button>
              )}
            </div>
            {/* Secondary admin tools — icon + text, readable row */}
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              {/* Closed while what they write is unread (#1359, #1362): their
                  saves would be refused, and what they would show is the
                  engine's defaults or an empty list, not this club's settings. */}
              <button
                onClick={() => setShowZoneEditor(true)}
                disabled={!pricesRead}
                title={pricesRead ? undefined : t('booking.view.configUnread.title')}
                className="text-xs px-2.5 py-1.5 rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/25 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('booking.view.zones', { n: String(zones.length) })}
              </button>
              <button
                onClick={() => setShowConfigEditor(true)}
                disabled={!bookingConfigRead}
                title={bookingConfigRead ? undefined : t('booking.view.configUnread.title')}
                className="text-xs px-2.5 py-1.5 rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/25 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('booking.view.settings')}
              </button>
              {onSaveAsset && onDeleteAsset && (
                <button
                  onClick={() => setShowAssetEditor(true)}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/25 transition-all whitespace-nowrap"
                >
                  {t('booking.view.assets')}
                </button>
              )}
              <button
                onClick={() => setShowHelp(true)}
                className="text-xs px-2.5 py-1.5 rounded-full bg-white/15 text-white border border-white/25 hover:bg-white/25 transition-all whitespace-nowrap"
              >
                {t('booking.view.help')}
              </button>
              {/* Demo tools — smaller, icon-only */}
              <button
                onClick={generateDemoBookings}
                title={t('booking.view.demoTitle')}
                // The glyph was the whole accessible name; the title only a description.
                aria-label={t('booking.view.demoTitle')}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 transition-all text-xs"
              >
                🎲
              </button>
              <button
                onClick={() => setConfirmClearAll(true)}
                title={t('booking.view.clearAllTitle')}
                aria-label={t('booking.view.clearAllTitle')}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 transition-all text-xs"
              >
                🧹
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Day quick buttons: days into the future with a color indicator for availability.
          Days outside the booking window are locked (padlock) for regular users.
          Shown in all views. The calendar square opens a date picker to
          jump to a specific date. */}
      <div className="flex items-stretch gap-1.5 mb-4 overflow-x-auto pb-1">
        {/* Calendar square: opens the browser's date picker to jump to a
              specific date. Same size as the day buttons. */}
        {(() => {
          // DEVIATION (lint): `maxIso` was assigned-but-never-read in the original artifact
          // (dead code); `_`-prefixed to satisfy no-unused-vars without changing
          // behaviour. Otherwise byte-identical.
          let _maxIso;
          if (!adminMode) {
            const m = new Date(todayIso + 'T00:00:00');
            m.setDate(m.getDate() + (bookingWindowDays - 1));
            _maxIso = toLocalIso(m);
          }
          return (
            <button
              type="button"
              title={t('booking.view.jumpToDate')}
              onClick={() => setShowDatePicker((v) => !v)}
              className={`relative flex-shrink-0 flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl border-2 border-dashed transition-all min-w-[3.5rem] cursor-pointer ${
                showDatePicker
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-ink-3/40 bg-bg-soft/30 hover:border-emerald-400 hover:bg-emerald-50/40'
              }`}
            >
              <Calendar size={18} className="text-ink-2" />
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-2 font-semibold">
                {t('booking.view.custom')}
              </span>
            </button>
          );
        })()}
        {quickDays.map((iso) => {
          const d = new Date(iso + 'T00:00:00');
          const wd = weekdayCap[d.getDay()];
          const isSel = iso === selectedDate;
          const isToday = iso === todayIso;
          const locked = isDateLocked(iso);
          const segs = dayAvailability[iso] || [1, 1, 1];
          // Five color steps for a smoother transition from free to full:
          //   > 80 %  → green   (plenty of room)
          //   60–80 % → lime    (a bit busy)
          //   40–60 % → yellow  (half full)
          //   20–40 % → orange  (filling up)
          //   < 20 %  → red     (nearly/completely full)
          // null = past segment (today) → gray
          const dotHex = (r) =>
            r == null
              ? '#cbd2cb'
              : r > 0.8
                ? '#16a34a' // green
                : r > 0.6
                  ? '#65a30d' // lime
                  : r > 0.4
                    ? '#eab308' // yellow
                    : r > 0.2
                      ? '#f97316' // orange
                      : '#dc2626'; // red
          const pct = (r) => (r == null ? t('booking.view.past') : `${Math.round(r * 100)}%`);
          return (
            <button
              key={iso}
              onClick={() => {
                if (!locked) setSelectedDate(iso);
              }}
              disabled={locked}
              className={`flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl border-2 transition-all min-w-[3.5rem] ${
                locked
                  ? 'border-line-soft bg-bg-soft/30 opacity-60 cursor-not-allowed'
                  : isSel
                    ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                    : 'border-line bg-white hover:border-emerald-300 hover:bg-emerald-50/40'
              }`}
              title={
                locked
                  ? t('booking.view.dayTitleLocked', {
                      date: `${wd} ${d.getDate()}.${d.getMonth() + 1}`,
                      days: String(bookingWindowDays),
                    })
                  : t('booking.view.dayTitleFree', {
                      date: `${wd} ${d.getDate()}.${d.getMonth() + 1}`,
                      early: pct(segs[0]),
                      mid: pct(segs[1]),
                      evening: pct(segs[2]),
                    })
              }
            >
              <span
                className={`text-[10px] font-mono uppercase tracking-wider ${isToday ? 'text-emerald-700 font-bold' : 'text-ink-3'}`}
              >
                {isToday ? t('booking.view.today') : wd}
              </span>
              <span
                className={`font-display text-lg font-bold leading-none ${locked ? 'text-ink-3' : isSel ? 'text-emerald-700' : 'text-ink-1'}`}
              >
                {d.getDate()}
              </span>
              {locked ? (
                <Lock size={11} className="text-ink-3" />
              ) : (
                <span className="flex items-center gap-0.5" title={t('booking.view.dotsTitle')}>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: dotHex(segs[0]) }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: dotHex(segs[1]) }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: dotHex(segs[2]) }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom date — its own calendar popover. The native date picker does not
          work reliably in the sandboxed iframe (showPicker is blocked), so we build
          a simple month calendar ourselves. */}
      {showDatePicker && (
        <CalendarPopover
          selectedDate={selectedDate}
          todayIso={todayIso}
          maxDays={adminMode ? null : bookingWindowDays}
          toLocalIso={toLocalIso}
          onPick={(iso) => {
            setSelectedDate(iso);
            setShowDatePicker(false);
          }}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {/* View mode toggle — below the date strip */}
      <div className="flex items-center gap-1 mb-4 p-1 rounded-lg bg-bg-soft/50 border border-line w-fit flex-wrap">
        <button
          onClick={() => setViewMode('slots')}
          className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${viewMode === 'slots' ? 'bg-white shadow text-ink' : 'text-ink-3 hover:text-ink-1'}`}
        >
          {t('booking.view.viewSlots')}
        </button>
        <button
          onClick={() => setViewMode('matrix')}
          className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${viewMode === 'matrix' ? 'bg-white shadow text-ink' : 'text-ink-3 hover:text-ink-1'}`}
        >
          {t('booking.view.viewMatrix')}
        </button>
        {/* Lane filter — only where there are equipment lanes to filter, and
            only in the calendar, which is where the lanes are drawn. */}
        {viewMode === 'matrix' && assetRows.length > 0 && (
          <div
            role="group"
            aria-label={t('booking.view.filter.label')}
            className="flex items-center gap-1 flex-wrap ml-2 pl-2 border-l border-line"
          >
            {(
              [
                ['all', 'booking.view.filter.all'],
                ['courts', 'booking.view.filter.courts'],
                ['assets', 'booking.view.filter.assets'],
              ] as const
            ).map(([value, key]) => (
              <button
                key={value}
                type="button"
                aria-pressed={effectiveLaneFilter === value}
                onClick={() => {
                  setLaneFilter(value);
                  // «Alt» means all of it, the kind included (#986).
                  if (value === 'all') setAssetKindFilter(null);
                }}
                className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${effectiveLaneFilter === value ? 'bg-white shadow text-ink' : 'text-ink-3 hover:text-ink-1'}`}
              >
                {t(key)}
              </button>
            ))}
            {effectiveLaneFilter !== 'courts' && assetKinds.length > 1 && (
              <>
                <button
                  type="button"
                  aria-pressed={effectiveAssetKindFilter == null}
                  onClick={() => setAssetKindFilter(null)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${effectiveAssetKindFilter == null ? 'bg-white shadow border-line text-ink' : 'border-transparent text-ink-3 hover:text-ink-1'}`}
                >
                  {t('booking.view.filter.kindAll')}
                </button>
                {assetKinds.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={effectiveAssetKindFilter === kind}
                    onClick={() => setAssetKindFilter(kind)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${effectiveAssetKindFilter === kind ? 'bg-white shadow border-line text-ink' : 'border-transparent text-ink-3 hover:text-ink-1'}`}
                  >
                    {assetKindLabel(t, kind)}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Date label in the slot and matrix views (both are day-based).
          The per-court calendar has its own day headers. */}
      {(viewMode === 'slots' || viewMode === 'matrix') && (
        <div className="text-lg font-display font-semibold text-ink-1 mb-3">
          {formatDateLabel(selectedDate)}
        </div>
      )}

      {/* Court matrix (matrix mode): day view with a timeline per court */}
      {viewMode === 'matrix' && (
        <div ref={matrixScrollRef} className="card-elev p-3 overflow-x-auto">
          <div style={{ minWidth: 80 + totalMin * PX_PER_MIN, position: 'relative' }}>
            {/* "Now" line through the whole matrix (only today, within opening hours) */}
            {nowWithinOpen && (
              <div
                className="absolute top-0 bottom-0 z-20 pointer-events-none"
                style={{ left: 80 + (nowMin - startMin) * PX_PER_MIN }}
              >
                <div style={{ width: 2, background: '#dc2626', height: '100%' }} />
                <div
                  className="absolute -top-0.5 -left-1 w-3 h-3 rounded-full"
                  style={{ background: '#dc2626' }}
                />
              </div>
            )}
            {/* CLOSING TIME, DRAWN RATHER THAN IMPLIED.
                The axis already ended at closing, but nothing SAID so — and an
                out-of-hours booking stretches the container past it, so the
                grid simply continued and the edge looked like the end of the
                data. An operator reading the calendar had no way to see where
                the centre shuts.
                Two marks: a line at closing, and a tint over everything after
                it. The tint is translucent and `pointer-events-none`, so the
                bookings that live out there stay readable and clickable —
                they exist, and hiding them would be a different lie. */}
            {closedBandLeft != null && (
              <div
                className="absolute top-0 bottom-0 z-[5] pointer-events-none"
                style={{ left: closedBandLeft, right: 0 }}
              >
                <div
                  className="absolute inset-y-0 left-0 right-0"
                  style={{
                    background:
                      'repeating-linear-gradient(135deg, rgba(74,84,76,0.07) 0 6px, transparent 6px 12px)',
                    borderLeft: '2px dashed #9aa79c',
                  }}
                />
                <span
                  className="absolute top-0 left-1 font-mono text-[10px] px-1 rounded-b"
                  style={{ background: '#4a544c', color: '#fff' }}
                >
                  {t('booking.view.closedFrom', { time: closeTime })}
                </span>
              </div>
            )}
            {/* Time headers */}
            <div className="flex border-b border-line mb-2">
              <div
                style={{
                  width: 80,
                  flexShrink: 0,
                  position: 'sticky',
                  left: 0,
                  zIndex: 15,
                  background: '#fff',
                }}
                className="font-mono text-[10px] text-ink-3 px-2 py-1"
              >
                {t('booking.view.courtHeader')}
              </div>
              <div className="relative flex-1" style={{ height: 28 }}>
                {hours.map((h) => {
                  const offsetMin = h * 60 - startMin;
                  return (
                    <div
                      key={h}
                      className="absolute font-mono text-[10px] text-ink-3 border-l border-line h-full pl-1"
                      style={{ left: offsetMin * PX_PER_MIN }}
                    >
                      {String(h % 24).padStart(2, '0')}:00
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Court rows */}
            {visibleCourts.map((court) => {
              const courtBookings = dayBookings.filter((b) => b.court === court);
              // Out of service (#271): the row stays, and says so.
              const broken = courtState(court);
              return (
                <div
                  key={court}
                  className="flex border-b border-line/40 last:border-b-0"
                  style={{ minHeight: 56 }}
                >
                  <div
                    style={{
                      width: 80,
                      flexShrink: 0,
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      background: broken ? '#fff7ed' : '#fff',
                    }}
                    className="font-mono text-xs text-ink-2 font-semibold px-2 py-3 flex flex-col gap-1 justify-center border-r border-line/40"
                  >
                    <span className={broken ? 'text-amber-800' : undefined}>{baneNavn(court)}</span>
                    {broken && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider rounded px-1 py-0.5 bg-amber-100 text-amber-800 leading-tight"
                        title={broken.reason || undefined}
                      >
                        {t('booking.view.outOfService')}
                      </span>
                    )}
                    <CourtFacilityIcons
                      courtNum={court}
                      courtMeta={courtMeta}
                      courtDefaults={cfg.courtDefaults}
                      size={12}
                    />
                    {adminMode && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCourtStateError(null);
                          setCourtStateEdit(
                            courtStateEdit?.court === court
                              ? null
                              : { court, reason: broken?.reason || '' },
                          );
                        }}
                        title={
                          broken
                            ? t('booking.view.reopenTitle', { court: baneNavn(court) })
                            : t('booking.view.closeTitle', { court: baneNavn(court) })
                        }
                        className={`text-[9px] font-mono uppercase tracking-wider rounded px-1 py-0.5 border ${
                          broken
                            ? 'border-amber-300 text-amber-800 hover:bg-amber-100'
                            : 'border-line text-ink-3 hover:bg-bg-soft'
                        }`}
                      >
                        {t(broken ? 'booking.view.open' : 'booking.view.close')}
                      </button>
                    )}
                  </div>
                  {/* THE LANE IS A POINTER SHORTCUT, NOT A CONTROL (#900). A click
                      on it opens «Ny booking» with this court and the clicked
                      hour filled in; the keyboard reaches the same form through
                      the «Ny booking» button above, and every booking in the
                      lane is a button of its own. So the lane is
                      role="presentation", not a fake control. */}
                  <div
                    role="presentation"
                    className={`relative flex-1 ${cfg.allowDirectBooking !== false || adminMode ? 'cursor-pointer hover:bg-bg-soft/20' : ''}`}
                    style={{
                      minHeight: 56,
                      // Hatched, not filled: the lane still has to read as a lane
                      // with bookings on it, and «this court is closed» is a
                      // property of the lane rather than of any one slot.
                      ...(broken
                        ? {
                            backgroundImage:
                              'repeating-linear-gradient(45deg, rgba(245,158,11,0.10) 0 6px, transparent 6px 12px)',
                          }
                        : null),
                    }}
                    onClick={(e) =>
                      handleEmptyClick(court, e.clientX, e.currentTarget.getBoundingClientRect())
                    }
                    onMouseMove={(e) =>
                      handleEmptyHover(court, e.clientX, e.currentTarget.getBoundingClientRect())
                    }
                    onMouseLeave={() => setHoverSlot(null)}
                  >
                    {/* Close / reopen the court (#271) — an inline panel, never
                        window.confirm/prompt (DOMAIN_RULES §9: blocked in an iframe). */}
                    {courtStateEdit?.court === court && (
                      // Its onClick only keeps a click in the panel from
                      // reaching the lane behind it (#900): not a control.
                      <div
                        role="presentation"
                        className="absolute left-2 top-1 z-30 bg-white border border-line rounded-lg shadow-lg p-2 flex flex-col gap-1.5"
                        style={{ width: 300, maxWidth: 'calc(100% - 16px)' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseMove={(e) => e.stopPropagation()}
                      >
                        <div
                          id={ids.courtState(court)}
                          className="text-[11px] font-semibold text-ink-1"
                        >
                          {broken
                            ? t('booking.view.courtIsOut', { court: baneNavn(court) })
                            : t('booking.view.closeTitle', { court: baneNavn(court) })}
                        </div>
                        <input
                          type="text"
                          aria-labelledby={ids.courtState(court)}
                          aria-describedby={
                            courtStateError
                              ? `${ids.courtStateHint(court)} ${ids.courtStateError(court)}`
                              : ids.courtStateHint(court)
                          }
                          aria-invalid={courtStateError ? true : undefined}
                          ref={courtStateInputRef}
                          className="input text-xs"
                          maxLength={200}
                          placeholder={t('booking.view.reasonPlaceholder')}
                          value={courtStateEdit.reason}
                          onChange={(e) => setCourtStateEdit({ court, reason: e.target.value })}
                        />
                        <div
                          id={ids.courtStateHint(court)}
                          className="text-[10px] text-ink-3 leading-tight"
                        >
                          {t('booking.view.noteStaffOnly')}
                        </div>
                        {courtStateError && (
                          <div
                            id={ids.courtStateError(court)}
                            className="text-[10px] text-rose leading-tight"
                          >
                            {courtStateError}
                          </div>
                        )}
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            disabled={courtStateBusy}
                            onClick={() => applyCourtState(court, courtStateEdit.reason)}
                            className="text-[11px] font-semibold rounded px-2 py-1 bg-amber-500 text-white disabled:opacity-60"
                          >
                            {t(broken ? 'booking.view.updateNote' : 'booking.view.markOut')}
                          </button>
                          {broken && (
                            <button
                              type="button"
                              disabled={courtStateBusy}
                              onClick={() => applyCourtState(court, null)}
                              className="text-[11px] font-semibold rounded px-2 py-1 bg-emerald-700 text-white disabled:opacity-60"
                            >
                              {t('booking.view.setInService')}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={courtStateBusy}
                            onClick={() => setCourtStateEdit(null)}
                            className="text-[11px] rounded px-2 py-1 border border-line text-ink-2"
                          >
                            {t('booking.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {/* The staff note, where staff are already looking (#271). It sits
                        BEHIND the bookings — a closed court usually has none, and when
                        it does, the bookings are the more urgent thing to read. */}
                    {broken?.reason && (
                      <div
                        className="absolute left-2 top-1 text-[10px] text-amber-800/90 font-mono truncate pointer-events-none"
                        style={{ zIndex: 0, maxWidth: 'calc(100% - 16px)' }}
                      >
                        {broken.reason}
                      </div>
                    )}
                    {/* Hover tooltip: possible durations from this point */}
                    {hoverSlot &&
                      hoverSlot.court === court &&
                      (cfg.allowDirectBooking !== false || adminMode) &&
                      (() => {
                        const hasGreen = hoverSlot.green && hoverSlot.green.length > 0;
                        const hasRed =
                          !hasGreen &&
                          ((hoverSlot.red && hoverSlot.red.length > 0) || hoverSlot.suggestCourt);
                        const tip = hasGreen
                          ? `${fromMin(hoverSlot.startMin)} · ${hoverSlot.green.map((d) => `${d}m`).join(' / ')}`
                          : hasRed
                            ? hoverSlot.suggestCourt
                              ? t('booking.view.hoverSuggest', {
                                  court: baneNavn(hoverSlot.suggestCourt),
                                  dur: String(hoverSlot.suggestDur),
                                })
                              : t('booking.view.hoverIndivisible')
                            : t('booking.view.hoverNone', {
                                time: fromMin(hoverSlot.startMin),
                              });
                        // Clamp the tooltip's left edge so it does not overflow to the right.
                        const clampedLeft = Math.max(
                          0,
                          Math.min(hoverSlot.leftPx, totalMin * PX_PER_MIN - 200),
                        );
                        return (
                          <div
                            className="absolute z-30 pointer-events-none top-0 -translate-y-full pb-1"
                            style={{ left: clampedLeft, maxWidth: 200 }}
                          >
                            <div
                              className="px-2 py-1 rounded text-white text-[10px] font-mono shadow-lg"
                              style={{
                                background: hasRed ? '#dc2626' : 'var(--ink, #1a1a1a)',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                              }}
                            >
                              {tip}
                              {/* WHAT ADMIN MODE IS STANDING ON.
                                  Only reachable in admin mode — for everyone
                                  else these durations were filtered out before
                                  they could be offered. The point is that the
                                  override stays available and stops being
                                  silent: a green chip that breaks the centre's
                                  own rules should say which one. */}
                              {(hoverSlot.outsideHours?.length > 0 ||
                                hoverSlot.againstSmart?.length > 0) && (
                                <div className="mt-1 pt-1 border-t border-white/25 text-[10px] leading-snug">
                                  <div className="opacity-80">{t('booking.view.overrideLead')}</div>
                                  {hoverSlot.outsideHours?.length > 0 && (
                                    <div>
                                      {t('booking.view.overrideHours', {
                                        open: openTime,
                                        close: closeTime,
                                      })}
                                    </div>
                                  )}
                                  {hoverSlot.againstSmart?.length > 0 && (
                                    <div>{t('booking.view.overrideSmart')}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    {/* Marker: green = bookable here, red = illegal here (indivisible gap / taken) */}
                    {hoverSlot &&
                      hoverSlot.court === court &&
                      (hoverSlot.green?.length > 0 ||
                        hoverSlot.red?.length > 0 ||
                        hoverSlot.suggestCourt) &&
                      (() => {
                        const hasGreen = hoverSlot.green && hoverSlot.green.length > 0;
                        const durPx = hasGreen
                          ? Math.max(...hoverSlot.green)
                          : hoverSlot.red && hoverSlot.red.length > 0
                            ? Math.max(...hoverSlot.red)
                            : hoverSlot.suggestDur || 60;
                        const color = hasGreen ? 'var(--lime-deep)' : '#dc2626';
                        return (
                          <div
                            className="absolute top-1 bottom-1 rounded pointer-events-none border-2 border-dashed"
                            style={{
                              left: hoverSlot.leftPx,
                              width: durPx * PX_PER_MIN,
                              borderColor: color,
                              background: `color-mix(in srgb, ${color} 10%, transparent)`,
                            }}
                          />
                        );
                      })()}
                    {/* Background grid (each hour) */}
                    {hours.map((h) => {
                      const offsetMin = h * 60 - startMin;
                      return (
                        <div
                          key={h}
                          className="absolute top-0 bottom-0 border-l border-line/30"
                          style={{ left: offsetMin * PX_PER_MIN }}
                        />
                      );
                    })}
                    {/* Bookings */}
                    {courtBookings.map((b) => {
                      let bStart = toMin(b.startTime);
                      let bEnd = toMin(b.endTime);
                      // Over-midnight matrix: bookings with a start time before opening
                      // (e.g. 00:30 when the center opens 05:00) belong to the late
                      // part of the axis → add 1440. Also bookings that themselves
                      // cross midnight (end < start).
                      if (endMin > 1440 && bStart < startMin) {
                        bStart += 1440;
                        bEnd += 1440;
                      } else if (bEnd < bStart) bEnd += 1440;
                      const left = (bStart - startMin) * PX_PER_MIN;
                      const width = (bEnd - bStart) * PX_PER_MIN;
                      // Historical = finished before now (only relevant today). Grayed out.
                      const isPast = isViewingToday && bEnd <= nowMin;
                      return (
                        <button
                          key={b.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Series booking: ask for the scope first. Otherwise open directly.
                            if (b.seriesId) {
                              setSeriesChoice(b);
                            } else {
                              setEditScope('single');
                              setEditingBooking(b);
                            }
                          }}
                          className={`absolute top-1 bottom-1 rounded text-white text-[11px] px-2 py-1 text-left hover:shadow-md transition-all overflow-hidden ${isPast ? 'opacity-45' : ''}`}
                          style={{
                            left,
                            width,
                            background: isPast ? '#9ca3af' : bookingSourceStyle(b).bg,
                          }}
                          title={`${b.startTime}–${b.endTime} · ${b.title || b.bookedBy?.name || t('booking.view.anonymous')} · ${isSingleMatchBooking(b) ? t('booking.view.singleMatch') : bookingSourceStyle(b).label}${b.priceTier === 'guest' ? ` · ${t('booking.view.guestBadge')}` : ''}${isPast ? t('booking.view.passedSuffix') : ''}`}
                        >
                          <div className="font-semibold truncate flex items-center gap-1">
                            {b.serieMatchId ? (
                              <Trophy size={10} className="flex-shrink-0 opacity-90" />
                            ) : (
                              b.seriesId && (
                                <RotateCcw size={10} className="flex-shrink-0 opacity-90" />
                              )
                            )}
                            {b.startTime}–{b.endTime}
                          </div>
                          {isSingleMatchBooking(b) && (
                            <div className="text-[9px] font-bold uppercase tracking-wide opacity-95">
                              {t('booking.view.singleMatchBadge')}
                            </div>
                          )}
                          {/* «GJEST» (#833 slice 3b): booked at the guest price by
                              somebody who is not a member here — the server's
                              stored tier, never inferred from a name. DISPLAY
                              ONLY. Dark on white rather than white on the block,
                              so it reads on every source colour and on a past
                              booking's grey alike. */}
                          {b.priceTier === 'guest' && (
                            <div>
                              <span
                                className="inline-block text-[9px] font-bold uppercase tracking-wide px-1 rounded-sm bg-white text-[#0f1611]"
                                data-testid="booking-guest-badge"
                              >
                                {t('booking.view.guestBadge')}
                              </span>
                            </div>
                          )}
                          <div className="opacity-90 truncate text-[10px]">
                            {b.title || b.bookedBy?.name || '—'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* EQUIPMENT LANES (docs/domains/ASSET_BOOKING.md) — one per asset kept
                at this venue, under the courts, on the same time axis. A click on
                an empty stretch books the asset from that half hour; a block opens
                the reservation. */}
            {visibleAssetRows.length > 0 && (
              <>
                <div className="flex border-b border-line mt-3 mb-1">
                  <div
                    style={{
                      width: 80,
                      flexShrink: 0,
                      position: 'sticky',
                      left: 0,
                      zIndex: 15,
                      background: '#fff',
                    }}
                    className="font-mono text-[10px] text-ink-3 px-2 py-1"
                  >
                    {t('booking.view.assetHeader')}
                  </div>
                </div>
                {visibleAssetRows.map((asset) => {
                  const rows = dayAssetBookings.filter((b) => b.assetId === asset.id);
                  const clickable = asset.active !== false && Boolean(onAddAssetBooking);
                  return (
                    <div
                      key={asset.id}
                      className="flex border-b border-line/40 last:border-b-0"
                      style={{ minHeight: 48 }}
                    >
                      <div
                        style={{
                          width: 80,
                          flexShrink: 0,
                          position: 'sticky',
                          left: 0,
                          zIndex: 10,
                          background: '#fff',
                        }}
                        className="font-mono text-xs text-ink-2 font-semibold px-2 py-3 flex flex-col gap-1 justify-center border-r border-line/40"
                      >
                        <span className="truncate" title={asset.name}>
                          {asset.name}
                        </span>
                        {asset.active === false && (
                          <span className="text-[9px] font-mono uppercase tracking-wider rounded px-1 py-0.5 bg-amber-100 text-amber-800 leading-tight">
                            {t('booking.view.assetRowInactive')}
                          </span>
                        )}
                      </div>
                      <div className="relative flex-1" style={{ minHeight: 48 }}>
                        {hours.map((h) => (
                          <div
                            key={h}
                            className="absolute top-0 bottom-0 border-l border-line/30"
                            style={{ left: (h * 60 - startMin) * PX_PER_MIN }}
                          />
                        ))}
                        {/* The free stretch of the lane is a BUTTON, not a clickable div:
                            it has a name, it takes focus and a keypress books from the
                            start of the day; a pointer books from the half hour under it.
                            The reservations below sit on top of it in paint order. */}
                        {clickable && (
                          <button
                            type="button"
                            aria-label={t('booking.view.assetLaneBook', { asset: asset.name })}
                            className="absolute inset-0 w-full h-full cursor-pointer hover:bg-bg-soft/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-500"
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              // A keyboard activation has no pointer position (clientX is
                              // 0): then the lane's first half hour is the sensible start.
                              const offsetPx = e.clientX > 0 ? e.clientX - rect.left : 0;
                              const atMin =
                                startMin + Math.max(0, Math.floor(offsetPx / PX_PER_MIN / 30)) * 30;
                              setAssetBookingModal({
                                mode: 'create',
                                initial: {
                                  assetId: asset.id,
                                  date: selectedDate,
                                  startTime: fromMin(atMin % 1440),
                                  durationMin: 60,
                                },
                              });
                            }}
                          />
                        )}
                        {rows.map((b) => {
                          let bStart = toMin(b.startTime);
                          let bEnd = toMin(b.endTime);
                          if (endMin > 1440 && bStart < startMin) {
                            bStart += 1440;
                            bEnd += 1440;
                          } else if (bEnd < bStart) bEnd += 1440;
                          const left = (bStart - startMin) * PX_PER_MIN;
                          const width = (bEnd - bStart) * PX_PER_MIN;
                          const isPast = isViewingToday && bEnd <= nowMin;
                          const who =
                            b.title || b.bookedBy?.name || t('booking.assetBooking.anonymous');
                          const court = b.bookingId
                            ? bookings.find((cb) => String(cb.id) === String(b.bookingId))?.court
                            : null;
                          return (
                            <button
                              key={b.id}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssetBookingModal({ mode: 'edit', initial: b });
                              }}
                              className={`absolute top-1 bottom-1 rounded text-white text-[11px] px-2 py-1 text-left hover:shadow-md transition-all overflow-hidden ${isPast ? 'opacity-45' : ''}`}
                              style={{
                                left,
                                width,
                                background: isPast ? '#9ca3af' : ASSET_BLOCK_COLOR,
                              }}
                              title={t('booking.view.assetTitle', {
                                asset: asset.name,
                                start: b.startTime,
                                end: b.endTime,
                                who,
                              })}
                            >
                              <div className="font-semibold truncate">
                                {b.startTime}–{b.endTime}
                              </div>
                              <div className="opacity-90 truncate text-[10px]">
                                {who}
                                {court != null
                                  ? ` · ${t('booking.view.courtName', { n: String(court) })}`
                                  : ''}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          {/* Legend — color code by booking origin */}
          <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-line/40">
            {Object.entries(BOOKING_SOURCE_STYLES).map(([key, s]) => (
              <span key={key} className="flex items-center gap-1.5 text-[11px] text-ink-2">
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: s.bg }} />
                {s.label}
              </span>
            ))}
            {visibleAssetRows.length > 0 && (
              <span className="flex items-center gap-1.5 text-[11px] text-ink-2">
                <span
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ background: ASSET_BLOCK_COLOR }}
                />
                {t('booking.view.assetLegend')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Slot-aggregated view */}
      {viewMode === 'slots' && (
        <div className="card-elev p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h3 className="font-display text-xl font-semibold leading-tight">
                {t('booking.view.slotsTitle')}
              </h3>
              <p className="text-xs text-ink-3 mt-0.5">{t('booking.view.slotsLead')}</p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {allowedDurs.map((d) => {
                const on = selectedDurations.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggleDuration(d)}
                    aria-pressed={on}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border ${
                      on
                        ? 'bg-emerald-700 text-white shadow border-emerald-700'
                        : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border-line'
                    }`}
                  >
                    {on ? '✓ ' : ''}
                    {t('booking.view.minutes', { n: String(d) })}
                  </button>
                );
              })}
              <span className="w-px h-5 bg-line mx-0.5" aria-hidden />
              <button
                onClick={() => setGroupByDaypart((v) => !v)}
                aria-pressed={groupByDaypart}
                title={t('booking.view.groupTitle')}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border ${
                  groupByDaypart
                    ? 'bg-ink text-white shadow border-ink'
                    : 'bg-bg-soft/40 text-ink-2 hover:bg-bg-soft/70 border-line'
                }`}
              >
                {groupByDaypart ? '✓ ' : ''}
                {t('booking.view.grouping')}
              </button>
            </div>
          </div>
          {(() => {
            // THE ENGINE IS TOLD WHICH COURTS ARE REAL.
            //
            // This used to iterate `Math.max(totalCourts, ...effectiveCourts)`
            // with `enabledCourts: null`, on the reasoning that an unenabled
            // court number «counts as free … we filter the responses afterward».
            // Half right, and the half it missed removed most of the day from
            // «Ledige tider»: an unenabled court is not only in the RESULT, it
            // is a candidate in smart booking's comparison INSIDE
            // validateBooking — and filtering afterwards cannot undo a lock it
            // caused.
            //
            // A court with no bookings has `fragScore` 0, the best score there
            // is, so a court number the club does not use beat every real court
            // whose placement left any gap. The real courts were locked, the
            // phantom was filtered out, and the slot vanished — reported as
            // «bane 1 er ledig fra 10.30 til 20.00, men Ledige tider viser det
            // ikke». A club whose courtCount happens to equal the centre's
            // physical count never saw it, which is why nothing caught it.
            //
            // Same arguments the CALENDAR path passes (see `smartOpts`), which
            // is why that view was right all along. The two share an engine;
            // they did not share what they asked it.
            const minBM =
              cfg.allowedDurations && cfg.allowedDurations.length > 0
                ? Math.min(...cfg.allowedDurations)
                : 60;
            const findOpts = {
              openTime,
              closeTime,
              stepMin: 30,
              bufferMin: cfg.bufferMin,
              smartAllocation: cfg.smartAllocation !== false,
              minBookingMin: minBM,
              allowedDurations: allowedDurs,
              enabledCourts: effectiveCourts,
            };
            // Which durations to show: the ones the user has turned on, kept to
            // what the club offers. The intersection still matters even though
            // the chips are now built from `allowedDurs` — a selection stored
            // from before the club changed its durations outlives the chip.
            // Empty = none selected → prompt to choose at least one.
            const durations = selectedDurations.filter((d) => allowedDurs.includes(d));
            if (durations.length === 0) {
              return (
                <div className="text-center py-10 text-ink-3 text-sm italic">
                  {t('booking.view.pickDuration')}
                </div>
              );
            }

            // Build slots for each duration, and merge them per start time. For each
            // start we collect UNIQUE physical courts (a court that takes both 90 and 120
            // is counted only once) + which durations fit.
            const byStart: Record<string, any> = {}; // startTime -> { startTime, durations:Set, freeSet:Set, bookableSet:Set, bestDuration }
            durations.forEach((dur) => {
              const raw = findFreeSlots(
                bookings,
                selectedDate,
                effectiveCourts.length,
                dur,
                findOpts,
              );
              raw.forEach((s) => {
                const bookable = s.freeBaneList.filter((c) => effectiveCourts.includes(c));
                const physFree = (s.physicallyFreeList || s.freeBaneList).filter((c) =>
                  effectiveCourts.includes(c),
                );
                if (bookable.length === 0) return;
                if (!byStart[s.startTime]) {
                  byStart[s.startTime] = {
                    startTime: s.startTime,
                    durations: new Set(),
                    freeSet: new Set(),
                    bookableSet: new Set(),
                    bestDuration: dur,
                    bestEnd: s.endTime,
                  };
                }
                const entry = byStart[s.startTime];
                entry.durations.add(dur);
                physFree.forEach((c) => entry.freeSet.add(c));
                bookable.forEach((c) => entry.bookableSet.add(c));
                // Keep the longest possible duration as "best" (default on click)
                if (dur > entry.bestDuration) {
                  entry.bestDuration = dur;
                  entry.bestEnd = s.endTime;
                }
              });
            });
            // Hide past start times when the selected day is today. When it is 12:15,
            // the 11:00 or 12:00 slots should not be shown (they are over/in progress).
            // We round up to the next half-hour that can still be started.
            const now = new Date();
            const nowIso = toLocalIso(now);
            const nowMin = selectedDate === nowIso ? now.getHours() * 60 + now.getMinutes() : -1;

            const slots = Object.values(byStart)
              .map((e) => ({
                startTime: e.startTime,
                endTime: e.bestEnd,
                durations: [...e.durations].sort((a, b) => a - b),
                bestDuration: e.bestDuration,
                freeCourts: e.freeSet.size, // unique physical courts (not double-counted)
                bookableCourts: e.bookableSet.size,
                bookableList: [...e.bookableSet].sort((a, b) => a - b),
                totalCourts: effectiveCourts.length,
              }))
              .filter(
                (s) => s.bookableCourts > 0 && (nowMin < 0 || toMinHHMM(s.startTime) >= nowMin),
              )
              .sort((a, b) => toMinHHMM(a.startTime) - toMinHHMM(b.startTime));

            if (slots.length === 0) {
              const durLabel = t('booking.view.minutes', {
                n: durations.length > 1 ? durations.join('/') : String(durations[0]),
              });
              return (
                <div className="text-center py-10 text-ink-3 text-sm italic">
                  {t('booking.view.noSlots', { dur: durLabel })}
                </div>
              );
            }
            // Group slots by day part (morning / late morning / afternoon / evening) —
            // or, with the «Inndeling» toggle off, one flat grid without headings.
            const grouped: Record<string, { label: string; range: string; items: any[] }> =
              groupByDaypart
                ? {
                    morgen: {
                      label: t('booking.view.daypart.morning'),
                      range: t('booking.view.daypart.morningRange'),
                      items: [],
                    },
                    formiddag: {
                      label: t('booking.view.daypart.lateMorning'),
                      range: t('booking.view.daypart.lateMorningRange'),
                      items: [],
                    },
                    ettermiddag: {
                      label: t('booking.view.daypart.afternoon'),
                      range: t('booking.view.daypart.afternoonRange'),
                      items: [],
                    },
                    kveld: {
                      label: t('booking.view.daypart.evening'),
                      range: t('booking.view.daypart.eveningRange'),
                      items: [],
                    },
                  }
                : { alle: { label: '', range: '', items: [] } };
            slots.forEach((s) => {
              if (!groupByDaypart) {
                grouped.alle.items.push(s);
                return;
              }
              const h = toMinHHMM(s.startTime) / 60;
              if (h < 12) grouped.morgen.items.push(s);
              else if (h < 15) grouped.formiddag.items.push(s);
              else if (h < 18) grouped.ettermiddag.items.push(s);
              else grouped.kveld.items.push(s);
            });
            return (
              <div className="space-y-5">
                {/* Color legend for the availability ratio */}
                <div className="flex items-center gap-2.5 text-[10px] text-ink-3 flex-wrap">
                  <span className="font-mono uppercase tracking-wider">
                    {t('booking.view.availability')}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: '#16a34a' }} />{' '}
                    {t('booking.view.legendGood')}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: '#eab308' }} />{' '}
                    {t('booking.view.legendHalf')}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: '#f97316' }} />{' '}
                    {t('booking.view.legendFilling')}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: '#dc2626' }} />{' '}
                    {t('booking.view.legendAlmostFull')}
                  </span>
                </div>
                {Object.entries(grouped)
                  .filter(([_, g]) => g.items.length > 0)
                  .map(([key, g]) => (
                    <div key={key}>
                      {g.label && (
                        <div className="flex items-baseline justify-between mb-2">
                          <div className="font-display text-base font-semibold text-ink-1">
                            {g.label}
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                            {g.range}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {g.items.map((s) => {
                          // Null when the club has no saved zone (#1312):
                          // «Pris ikke satt», never «free» or a guess.
                          const price = calcCourtBookingPrice(
                            zones,
                            selectedDate,
                            s.startTime,
                            s.endTime,
                            cfg,
                          );
                          const dispTotal = applySerieDiscount(price?.total ?? 0);
                          const allFree = s.freeCourts === s.totalCourts;
                          // Five color steps by availability ratio (same scale as
                          // the day dots): green → lime → yellow → orange → red.
                          const ratio = s.totalCourts > 0 ? s.freeCourts / s.totalCourts : 0;
                          // Green when more than half the courts are free; then
                          // gradually down toward red as it fills up.
                          //   > 50 %  → green   (plenty of room)
                          //   35–50 % → yellow  (soon half full)
                          //   20–35 % → orange  (filling up)
                          //   < 20 %  → red     (nearly/completely full)
                          const hex =
                            ratio > 0.5
                              ? '#16a34a'
                              : ratio > 0.35
                                ? '#eab308'
                                : ratio > 0.2
                                  ? '#f97316'
                                  : '#dc2626';
                          // The availability LINE is text, so it takes the AA
                          // shade of the dot's hue (#1144, 1.4.3): the dot
                          // colours were 1.92–3.30:1 as 10 px words on the white
                          // card. The dot and the border keep the palette.
                          const textHex = SLOT_AVAILABILITY_TEXT[hex] ?? hex;
                          // When "Alle" is selected: show which durations fit.
                          const showDurations = durations.length > 1 && s.durations.length > 0;
                          // Equipment free for this slot's range — the same answer the
                          // picker in the modal gives (docs/domains/ASSET_BOOKING.md).
                          const freeAssets =
                            bookableAssets.length > 0
                              ? availableAssetsFor(bookableAssets, dayAssetBookings, {
                                  date: selectedDate,
                                  startTime: s.startTime,
                                  endTime: s.endTime,
                                }).length
                              : 0;
                          return (
                            <button
                              key={s.startTime}
                              onClick={() => {
                                setAddInitial({
                                  court: s.bookableList[0],
                                  startTime: s.startTime,
                                  date: selectedDate,
                                  durationMin: s.bestDuration,
                                });
                                setShowAddModal(true);
                              }}
                              className="relative rounded-2xl border-2 py-3 px-2 text-center transition-all hover:-translate-y-0.5 hover:shadow-md bg-white"
                              style={{ borderColor: hex + '66' }}
                              title={t('booking.view.slotTitle', {
                                time: s.startTime,
                                courts: t(
                                  s.freeCourts === 1
                                    ? 'booking.view.slotCourtsOne'
                                    : 'booking.view.slotCourtsMany',
                                  { n: String(s.freeCourts), total: String(s.totalCourts) },
                                ),
                                durations: showDurations
                                  ? t('booking.view.slotDurations', {
                                      list: s.durations.join('/'),
                                    })
                                  : '',
                                price: !price
                                  ? t('booking.view.priceNotSet')
                                  : dispTotal > 0
                                    ? serieDiscount > 0
                                      ? t('booking.view.slotPriceDiscount', {
                                          n: String(dispTotal),
                                          pct: String(serieDiscount),
                                        })
                                      : t('booking.view.priceKr', { n: String(dispTotal) })
                                    : t('booking.view.freeLower'),
                              })}
                            >
                              {/* Small availability dot in the top right */}
                              <span
                                className="absolute top-2 right-2 w-2 h-2 rounded-full"
                                style={{ background: hex }}
                              />
                              <div className="font-display text-2xl md:text-3xl font-bold text-ink-1 leading-none tracking-tight">
                                {s.startTime}
                              </div>
                              {showDurations ? (
                                <div className="text-[10px] font-semibold text-ink-2 mt-1">
                                  {t('booking.view.minutes', {
                                    n: s.durations.join(' / '),
                                  })}
                                </div>
                              ) : !price ? (
                                <div className="text-xs font-semibold text-ink-3 mt-1.5">
                                  {t('booking.view.priceNotSet')}
                                </div>
                              ) : dispTotal > 0 ? (
                                <div className="text-xs font-semibold text-emerald-700 mt-1.5">
                                  {t('booking.view.priceKr', { n: String(dispTotal) })}
                                  {serieDiscount > 0 && price.total > dispTotal ? (
                                    <span className="text-ink-4 line-through ml-1 font-normal">
                                      {price.total}
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                // emerald-700, as the price line: -600 was 3.77:1 (#1144)
                                <div className="text-xs font-semibold text-emerald-700 mt-1.5">
                                  {t('booking.view.free')}
                                </div>
                              )}
                              <div
                                className="text-[10px] mt-0.5 font-medium"
                                style={{ color: textHex }}
                              >
                                {allFree
                                  ? t('booking.view.allCourts')
                                  : s.freeCourts === 1
                                    ? t('booking.view.lastCourt')
                                    : t('booking.view.someFree', {
                                        n: String(s.freeCourts),
                                        total: String(s.totalCourts),
                                      })}
                              </div>
                              {bookableAssets.length > 0 && (
                                <div className="text-[10px] mt-0.5 text-ink-3">
                                  {freeAssets > 0
                                    ? t('booking.view.slotAssetsFree', { n: String(freeAssets) })
                                    : t('booking.view.slotAssetsNone')}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            );
          })()}
        </div>
      )}

      <p className="text-[11px] text-ink-3 mt-3 italic">
        {t(viewMode === 'calendar' ? 'booking.view.footerCalendar' : 'booking.view.footerSlots')}
      </p>

      {/* Add-modal */}
      {/* OVERRIDE A COURT THAT IS OUT OF SERVICE (#271).
          Shown only when the SERVER said this caller may — `overridable`. */}
      {/* A `Dialog` (#900). It had `role="dialog"` on the BACKDROP and no name;
          now the role and name sit on the panel, focus is trapped and returned,
          and Escape answers «Avbryt» — the same resolution as the button, so
          every call site sees the refusal it would have seen with no prompt.
          The backdrop still does not dismiss it: it never did. */}
      {overridePrompt && (
        <Dialog
          labelledBy={ids.overrideTitle}
          describedBy={ids.overrideBody}
          onClose={() => {
            const { resolve, refused } = overridePrompt;
            setOverridePrompt(null);
            resolve(refused);
          }}
          closeOnBackdropClick={false}
          showClose={false}
          backdropClassName="fixed inset-0 flex items-center justify-center bg-black/40 p-4"
          // ABOVE THE BOOKING MODAL (#908 review). The prompt opens while the
          // create/edit modal is still mounted at z-index 9999 (its confirm at
          // 10002); at the old z-[60] it rendered BEHIND it, invisible — and as
          // the innermost Dialog it owns Tab and Escape, so a keyboard user was
          // inside a dialog they could not see. Inline so a test can compare it.
          backdropStyle={{ zIndex: 10003 }}
          className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-3 overflow-y-auto max-h-[92vh]"
          style={{ maxHeight: 'calc(100dvh - 2rem)' }}
        >
          <h3 id={ids.overrideTitle} className="font-display text-lg font-semibold text-ink-1">
            {t('booking.view.overrideTitle', {
              court: String(overridePrompt.refusal.court),
            })}
          </h3>
          {overridePrompt.refusal.reason && (
            <div className="text-sm text-ink-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              {overridePrompt.refusal.reason}
            </div>
          )}
          <p id={ids.overrideBody} className="text-sm text-ink-2 leading-relaxed">
            {t('booking.view.overrideBody')}
          </p>
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => {
                // Cancelling resolves the ORIGINAL refusal, so every call site
                // sees the failure it would have seen without the prompt.
                const { resolve, refused } = overridePrompt;
                setOverridePrompt(null);
                resolve(refused);
              }}
            >
              {t('booking.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={async () => {
                const { resolve, retry } = overridePrompt;
                setOverridePrompt(null);
                resolve(await retry());
              }}
            >
              {t('booking.view.overrideConfirm')}
            </button>
          </div>
        </Dialog>
      )}
      {showAddModal && (
        <BookingEditModal
          mode="create"
          initial={addInitial}
          totalCourts={totalCourts}
          zones={zones}
          adminMode={adminMode}
          bookings={bookings}
          config={cfg}
          defaultDate={selectedDate}
          players={players}
          onPersistNewPlayer={onPersistNewPlayer}
          assets={bookableAssets}
          assetBookings={assetBookings}
          onClose={() => {
            setShowAddModal(false);
            setAddInitial(null);
          }}
          onSave={async (rawData) => {
            // Equipment chosen with the booking is a side channel, like the
            // match steps: it never enters the booking record.
            const { assetIds, ...data } = rawData;
            if (adminMode) {
              const { recur, courts: multiCourtsList, ...base } = data;
              let dates = recur?.enabled ? expandRecurrence(base.date, recur) : [base.date];
              // Avoid holidays if chosen — use the club's holiday set.
              if (recur?.enabled && recur.avoidHolidays) {
                dates = dates.filter((iso) => !isHoliday(iso, cfg));
              }
              const courtList =
                multiCourtsList && multiCourtsList.length > 0 ? multiCourtsList : [base.court];

              // Build all (date × court) combinations and mark which ones
              // collide with an existing booking (pure overlap check).
              const combos = [];
              for (const iso of dates) {
                for (const c of courtList) {
                  const cand = {
                    court: c,
                    date: iso,
                    startTime: base.startTime,
                    endTime: base.endTime,
                  };
                  const overlap = !validateBooking(cand, bookings, {
                    bufferMin: 0,
                    smartAllocation: false,
                  }).ok;
                  // Find who it clashes with (for display)
                  let conflictWith = null;
                  if (overlap) {
                    const sM = toMinHHMM(base.startTime),
                      eM = toMinHHMM(base.endTime);
                    const hit = bookings.find(
                      (b) =>
                        b.court === c &&
                        b.date === iso &&
                        toMinHHMM(b.startTime) < eM &&
                        toMinHHMM(b.endTime) > sM,
                    );
                    if (hit) {
                      const who =
                        hit.title || hit.bookedBy?.name || t('booking.view.conflictBooked');
                      conflictWith = { who, time: `${hit.startTime}–${hit.endTime}` };
                    } else {
                      conflictWith = { who: t('booking.view.conflictBusy'), time: '' };
                    }
                  }
                  combos.push({ iso, court: c, overlap, conflictWith });
                }
              }

              // If it is a single booking (no recurring/multi): book directly.
              if (combos.length === 1) {
                if (combos[0].overlap) {
                  alert(t('booking.view.alert.collision'));
                  return;
                }
                const single = { ...base, court: courtList[0], date: dates[0] };
                const r = await onAddBooking(single, { adminOverride: true });
                if (r.success) {
                  await attachAssets(r.booking?.id || r.id, single, assetIds);
                  setShowAddModal(false);
                  setAddInitial(null);
                } else alert(t('booking.view.alert.createFailed', { error: String(r.error) }));
                return;
              }

              // Multiple bookings: open the collision overview for the choice.
              setRecurPreview({ base, combos, recur });
              return;
            }
            // Normal flow: close the modal, open the payment modal with the booking data.
            // If the price is 0, create directly as 'free'. A club with NO saved
            // price zone has no price at all (#1312, owner decision 2026-11-03),
            // and nothing is charged: it takes the same free path, priced 0 —
            // never the default rate or an invented zone's.
            // Strip the optional match side-channels — they must NOT enter the
            // booking record; we act on them after the booking saves.
            const { plannedMatch, activityMatch, ...bookingData } = data;
            const price = calcCourtBookingPrice(
              zones,
              bookingData.date,
              bookingData.startTime,
              bookingData.endTime,
              cfg,
            ) ?? { total: 0, breakdown: [] };
            const serieTag = serieContext
              ? {
                  seriesId: serieContext.seriesId,
                  source: 'tournament',
                  serieMatchId: serieContext.matchId,
                  title: bookingData.title || serieContext.title,
                }
              : {};

            // VIA-AKTIVITETER: create a single-match activity linked to this booking.
            if (activityMatch?.enabled && onCreateLinkedActivity) {
              const activityId = `act_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
              // The booking is tagged with the activity id + a marker so the
              // activity auto-cancel/recurrence logic never deletes/clones the
              // user's own court reservation.
              const linkTag = {
                seriesId: activityId,
                source: 'activity_linked',
                title: `Enkeltkamp ${bookingData.date}`,
              };
              const buildCfg = (bookingId) => ({
                name: `Enkeltkamp ${bookingData.date} ${bookingData.startTime}`,
                tournamentType: 'single_match',
                courts: 1,
                noCourts: false,
                maxPlayers: 4,
                minPlayers: 4,
                pricePerPlayer: activityMatch.pricePerPlayer,
                kMultiplier: (activityMatch.eloWeightPct ?? 200) / 100,
                eloWeightPct: activityMatch.eloWeightPct ?? 200,
                // Match rules (defaults for live scoring on Banepad).
                matchFormat: activityMatch.matchFormat || 'bo3_super',
                deuceMode: activityMatch.deuceMode || 'advantage',
                deregisterCutoffHours: activityMatch.deregisterCutoffHours,
                signupCloseHours: activityMatch.signupCloseHours,
                allowReserves: false,
                maxReserves: 0,
                levelMin: activityMatch.levelMin ?? null,
                levelMax: activityMatch.levelMax ?? null,
                description: '',
                activityDate: bookingData.date,
                activityTime: bookingData.startTime,
                activityEndTime: bookingData.endTime,
                paymentMode: activityMatch.paymentMode,
                bookerPaidAll: activityMatch.paymentMode === 'booker_all',
                bookerName: bookingData.bookedBy?.name || '',
                linkedBookingId: bookingId,
              });
              if (activityMatch.paymentMode === 'split') {
                // The booker is NOT charged the full price now — the court is
                // covered by the four players' shares under Aktiviteter. Create
                // the booking as 'free' (no payment modal).
                const r = await onAddBooking({
                  ...bookingData,
                  ...linkTag,
                  paymentStatus: 'free',
                  priceNok: price.total,
                });
                if (r.success) {
                  const bookingId = r.booking?.id || r.id;
                  try {
                    await onCreateLinkedActivity({ activityId, config: buildCfg(bookingId) });
                  } catch (e) {
                    console.error('[Padel24] Could not create the single-match activity:', e);
                  }
                  setShowAddModal(false);
                  setAddInitial(null);
                } else {
                  alert(t('booking.view.alert.createFailed', { error: String(r.error) }));
                }
                return;
              }
              // booker_all → normal full payment, then create the activity on success.
              setPendingPayment({
                data: { ...bookingData, ...linkTag },
                priceNok: applySerieDiscount(price.total),
                activity: { activityId, buildCfg },
              });
              setShowAddModal(false);
              return;
            }

            if (price.total === 0) {
              const r = await onAddBooking({
                ...bookingData,
                ...serieTag,
                paymentStatus: 'free',
                priceNok: 0,
              });
              if (r.success) {
                const bookingId = r.booking?.id || r.id;
                await attachAssets(bookingId, bookingData, assetIds);
                if (serieContext?.onBooked && bookingId)
                  serieContext.onBooked(bookingId, { ...bookingData, ...serieTag });
                if (plannedMatch && bookingId && onRegisterPlannedMatch) {
                  try {
                    await onRegisterPlannedMatch({ ...plannedMatch, bookingId });
                  } catch (e) {
                    console.error('[Padel24] Could not register the planned match:', e);
                  }
                }
                setShowAddModal(false);
                setAddInitial(null);
              } else {
                alert(t('booking.view.alert.createFailed', { error: String(r.error) }));
              }
              return;
            }
            // Close the add modal and open the payment modal (discounted price for series reservations)
            setPendingPayment({
              data: { ...bookingData, ...serieTag },
              priceNok: applySerieDiscount(price.total),
              plannedMatch: plannedMatch || null,
              assetIds: Array.isArray(assetIds) ? assetIds : [],
            });
            setShowAddModal(false);
          }}
        />
      )}

      {/* Confirmation: clear the whole calendar */}
      {/* Escape and the backdrop answer «Avbryt», as the backdrop always did;
          focus starts on «Avbryt», the first control (#900). */}
      {confirmClearAll && (
        <Dialog
          labelledBy={ids.clearAllTitle}
          describedBy={ids.clearAllBody}
          onClose={() => setConfirmClearAll(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15,22,17,0.6)',
          }}
          className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
          style={{
            maxHeight: 'calc(100dvh - 2rem)',
            background: '#fff',
            maxWidth: 360,
            width: '100%',
            borderRadius: 16,
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ padding: '18px 20px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={20} style={{ color: '#dc2626' }} />
              </span>
              <h3
                id={ids.clearAllTitle}
                className="font-display text-lg font-semibold"
                style={{ color: '#1a1f1c' }}
              >
                {t('booking.view.clearAll.title')}
              </h3>
            </div>
            <p id={ids.clearAllBody} className="text-sm text-ink-2" style={{ lineHeight: 1.45 }}>
              {t('booking.view.clearAll.before')}{' '}
              <strong>
                {t('booking.view.clearAll.strong', { count: String(bookings.length) })}
              </strong>{' '}
              {t('booking.view.clearAll.after')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, padding: '0 20px 18px' }}>
            <button
              onClick={() => setConfirmClearAll(false)}
              className="flex-1 text-sm font-semibold py-2.5 rounded-lg"
              style={{ border: '1px solid #d7dad3', color: '#4a544c', background: '#fff' }}
            >
              {t('booking.cancel')}
            </button>
            <button
              onClick={clearAllBookings}
              className="flex-1 text-sm font-semibold py-2.5 rounded-lg"
              style={{ background: '#dc2626', color: '#fff', border: 'none' }}
            >
              {t('booking.view.clearAll.confirm')}
            </button>
          </div>
        </Dialog>
      )}

      {/* Series choice: "this occurrence" or "the whole series" */}
      {seriesChoice &&
        (() => {
          const isSerieMatch = !!seriesChoice.serieMatchId;
          const seriesCount = isSerieMatch
            ? 1
            : bookings.filter((b) => b.seriesId === seriesChoice.seriesId).length;
          if (isSerieMatch) {
            // Single event (series match) — NOT a recurring series. Offer only
            // «go to the match program» + edit/cancel this booking.
            return (
              <Dialog
                labelledBy={ids.seriesTitle}
                describedBy={ids.seriesLead}
                onClose={() => setSeriesChoice(null)}
                showClose={false}
                backdropClassName=""
                backdropStyle={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 10001,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  background: 'rgba(15,22,17,0.55)',
                }}
                className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
                style={{
                  maxHeight: 'calc(100dvh - 2rem)',
                  background: '#fff',
                  maxWidth: 380,
                  width: '100%',
                  borderRadius: 16,
                  boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
                }}
              >
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e7eae3',
                    background: '#f7fee7',
                  }}
                >
                  <h3
                    id={ids.seriesTitle}
                    className="font-display text-lg font-semibold flex items-center gap-2"
                    style={{ color: '#3f6212' }}
                  >
                    <Trophy size={16} /> {t('booking.view.serieMatch.title')}
                  </h3>
                  <p id={ids.seriesLead} className="text-xs text-ink-3 mt-0.5">
                    {seriesChoice.title || t('booking.view.serieMatch.sub')}
                  </p>
                </div>
                <div
                  style={{
                    padding: '14px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <button
                    onClick={() => {
                      onOpenTournament?.(seriesChoice.seriesId, seriesChoice.serieMatchId);
                      setSeriesChoice(null);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all flex items-center gap-2"
                    style={{ borderColor: 'var(--lime-deep)', background: 'var(--lime-bg)' }}
                  >
                    <Trophy
                      size={16}
                      style={{ color: 'var(--lime-text)' }}
                      className="flex-shrink-0"
                    />
                    <span>
                      <span className="font-semibold text-sm text-ink-1 block">
                        {t('booking.view.serieMatch.goProgram')}
                      </span>
                      <span className="text-[11px] text-ink-3">
                        {t('booking.view.serieMatch.openMatch')}
                      </span>
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setEditScope('single');
                      setEditingBooking(seriesChoice);
                      setSeriesChoice(null);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg border-2 border-line hover:border-emerald-300 hover:bg-emerald-50/40 transition-all"
                  >
                    <div className="font-semibold text-sm text-ink-1">
                      {t('booking.view.serieMatch.edit')}
                    </div>
                    <div className="text-[11px] text-ink-3">
                      {t('booking.view.serieMatch.editSub', { date: seriesChoice.date })}
                    </div>
                  </button>
                  <button
                    onClick={() => setSeriesChoice(null)}
                    className="text-xs text-ink-3 hover:text-ink-1 py-1 mt-1"
                  >
                    {t('booking.cancel')}
                  </button>
                </div>
              </Dialog>
            );
          }
          return (
            <Dialog
              labelledBy={ids.seriesTitle}
              describedBy={ids.seriesLead}
              onClose={() => setSeriesChoice(null)}
              showClose={false}
              backdropClassName=""
              backdropStyle={{
                position: 'fixed',
                inset: 0,
                zIndex: 10001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                background: 'rgba(15,22,17,0.55)',
              }}
              className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
              style={{
                maxHeight: 'calc(100dvh - 2rem)',
                background: '#fff',
                maxWidth: 380,
                width: '100%',
                borderRadius: 16,
                boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
              }}
            >
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e7eae3',
                  background: '#fafbf7',
                }}
              >
                <h3
                  id={ids.seriesTitle}
                  className="font-display text-lg font-semibold flex items-center gap-2"
                >
                  <RotateCcw size={16} /> {t('booking.view.series.title')}
                </h3>
                <p id={ids.seriesLead} className="text-xs text-ink-3 mt-0.5">
                  {t('booking.view.series.lead', { count: String(seriesCount) })}
                </p>
              </div>
              <div
                style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {seriesChoice.source === 'tournament' && seriesChoice.seriesId && (
                  <button
                    onClick={() => {
                      onOpenTournament?.(seriesChoice.seriesId);
                      setSeriesChoice(null);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all flex items-center gap-2"
                    style={{ borderColor: 'var(--lime-deep)', background: 'var(--lime-bg)' }}
                  >
                    <Trophy
                      size={16}
                      style={{ color: 'var(--lime-text)' }}
                      className="flex-shrink-0"
                    />
                    <span>
                      <span className="font-semibold text-sm text-ink-1 block">
                        {t('booking.view.series.goTournament')}
                      </span>
                      <span className="text-[11px] text-ink-3">
                        {seriesChoice.title || t('booking.view.series.openTournament')}
                      </span>
                    </span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditScope('single');
                    setEditingBooking(seriesChoice);
                    setSeriesChoice(null);
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-lg border-2 border-line hover:border-emerald-300 hover:bg-emerald-50/40 transition-all"
                >
                  <div className="font-semibold text-sm text-ink-1">
                    {t('booking.view.series.onlyThis')}
                  </div>
                  <div className="text-[11px] text-ink-3">
                    {t('booking.view.series.onlyThisSub', { date: seriesChoice.date })}
                  </div>
                </button>
                <button
                  onClick={() => {
                    setEditScope('series');
                    setEditingBooking(seriesChoice);
                    setSeriesChoice(null);
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-lg border-2 border-emerald-200 bg-emerald-50/40 hover:border-emerald-400 transition-all"
                >
                  <div className="font-semibold text-sm text-emerald-800">
                    {t('booking.view.series.whole', { count: String(seriesCount) })}
                  </div>
                  <div className="text-[11px] text-ink-3">{t('booking.view.series.wholeSub')}</div>
                </button>
                <button
                  onClick={() => setSeriesChoice(null)}
                  className="text-xs text-ink-3 hover:text-ink-1 py-1 mt-1"
                >
                  {t('booking.cancel')}
                </button>
              </div>
            </Dialog>
          );
        })()}

      {/* Edit-modal */}
      {editingBooking && (
        <BookingEditModal
          mode="edit"
          initial={editingBooking}
          totalCourts={totalCourts}
          zones={zones}
          adminMode={adminMode}
          bookings={bookings}
          config={cfg}
          defaultDate={selectedDate}
          editScope={editingBooking.seriesId ? (editScope as any) : null}
          seriesCount={
            editingBooking.seriesId
              ? bookings.filter((b) => b.seriesId === editingBooking.seriesId).length
              : 0
          }
          assets={bookableAssets}
          assetBookings={assetBookings}
          attachedAssetBookings={assetBookingsAttachedTo(assetBookings, editingBooking.id)}
          onRemoveAssetBooking={onDeleteAssetBooking}
          // A PAID single booking is cancelled by refunding it (#284); a series
          // edit, an unpaid booking or a session without the right keeps delete.
          onRefund={
            staffRefund &&
            editingBooking.paymentStatus === 'paid' &&
            !(editScope === 'series' && editingBooking.seriesId)
              ? async () => {
                  const r = await refundStaffBooking(String(editingBooking.id));
                  // The server deleted the booking (or another press, the
                  // provider's webhook or reconciliation already had — then the
                  // route finds no booking): the calendar re-reads so it says so.
                  if (
                    r.state === 'refunded' ||
                    (r.state === 'refused' &&
                      (r.code === 'already_cancelled' || r.code === 'booking_not_found'))
                  ) {
                    await staffRefund.onRefunded(String(editingBooking.id));
                  }
                  return r;
                }
              : null
          }
          onRefundDone={() => setEditingBooking(null)}
          onClose={() => setEditingBooking(null)}
          onSave={async (rawData) => {
            const { assetIds, ...data } = rawData;
            if (editScope === 'series' && editingBooking.seriesId) {
              const sid = editingBooking.seriesId;
              const members = bookings.filter((b) => b.seriesId === sid);
              const { recur: newRecur, courts: _c, ...fields } = data;
              // Pattern changed? Rebuild the series from the new recur object.
              const patternChanged = newRecur && newRecur.enabled;
              if (patternChanged) {
                const anchorDate = fields.date || members.map((m) => m.date).sort()[0];
                let dates = expandRecurrence(anchorDate, newRecur);
                if (newRecur.avoidHolidays) {
                  dates = dates.filter((iso) => !isHoliday(iso, cfg));
                }
                const court = fields.court || members[0].court;
                const dataList = dates.map((iso) => ({
                  ...fields,
                  court,
                  date: iso,
                  seriesId: sid,
                  recur: newRecur,
                }));
                const r = onReplaceSeries
                  ? await onReplaceSeries(sid, dataList, { adminOverride: true })
                  : { success: false };
                setEditingBooking(null);
                if (r.success)
                  alert(
                    t('booking.view.alert.seriesRebuilt', {
                      created: String(r.created),
                      failed: r.failed
                        ? t('booking.view.alert.seriesFailedPart', { n: String(r.failed) })
                        : '',
                    }),
                  );
                else alert(replaceFailedSentence(r));
                return;
              }
              // Only shared fields changed: keep each occurrence's date/court,
              // rebuild the series with the new fields (batch).
              const dataList = members.map((m) => ({
                ...fields,
                date: m.date,
                court: m.court,
                seriesId: sid,
                ...(m.recur ? { recur: m.recur } : {}),
              }));
              const r = onReplaceSeries
                ? await onReplaceSeries(sid, dataList, { adminOverride: true })
                : { success: false };
              setEditingBooking(null);
              if (r.success)
                alert(
                  t('booking.view.alert.seriesUpdated', {
                    created: String(r.created),
                    total: String(members.length),
                    failed: r.failed
                      ? t('booking.view.alert.seriesFailedSentence', { n: String(r.failed) })
                      : '',
                  }),
                );
              else alert(replaceFailedSentence(r));
              return;
            }
            const r = await onUpdateBooking(editingBooking.id, data);
            if (r.success) {
              await attachAssets(editingBooking.id, { ...editingBooking, ...data }, assetIds);
              setEditingBooking(null);
            } else alert(t('booking.view.alert.updateFailed', { error: String(r.error) }));
          }}
          onDelete={async () => {
            if (editScope === 'series' && editingBooking.seriesId) {
              const ids = bookings
                .filter((b) => b.seriesId === editingBooking.seriesId)
                .map((b) => b.id);
              const r = onDeleteManyBookings ? await onDeleteManyBookings(ids) : { success: false };
              setEditingBooking(null);
              if (r.success)
                alert(t('booking.view.alert.seriesCancelled', { n: String(r.removed) }));
              else if (r.code === 'booking_has_payment') alert(hasPaymentSentence());
              else alert(t('booking.view.alert.seriesCancelFailed'));
              return;
            }
            const r = await onDeleteBooking(editingBooking.id);
            if (r.success) setEditingBooking(null);
            // A PAID booking is not deleted (#284): say which door is its door.
            else if (r.code === 'booking_has_payment') alert(hasPaymentSentence());
            else
              alert(
                t('booking.view.alert.deleteFailed', {
                  // Any other REFUSAL CODE from the server (#1310: a live payment
                  // that could not be cancelled first) is said as a sentence; any
                  // other failure keeps the text it had.
                  error:
                    r.code && knownErrorCodes().includes(r.code)
                      ? errorMessage(t, r.code)
                      : String(r.error),
                }),
              );
          }}
        />
      )}

      {/* Collision overview for recurring/multi-booking */}
      {recurPreview &&
        (() => {
          const { base, combos } = recurPreview;
          const conflicts = combos.filter((c) => c.overlap);
          const clean = combos.filter((c) => !c.overlap);
          const navn = (c) => courtDisplayName(c, cfg.courtMeta || {});
          const doCreate = async (mode) => {
            // mode: 'skip' = skip the colliding ones, 'all' = override (cancel the
            // ones in the way, then book all).
            const toBook = mode === 'all' ? combos : clean;
            // Recurring series: all bookings get the same seriesId so they can
            // be edited/deleted as a group later. (Only when it is a
            // recurring one across multiple dates — not for pure multi-court same day.)
            const uniqueDates = new Set(toBook.map((c) => c.iso));
            const isSeries = uniqueDates.size > 1;
            const seriesId = isSeries
              ? `ser_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
              : null;

            // On override: find all existing bookings that overlap one
            // of the combinations — these are deleted in the same batch as the creation.
            let deleteIds = [];
            if (mode === 'all') {
              const sM = toMinHHMM(base.startTime);
              const eM = toMinHHMM(base.endTime);
              deleteIds = bookings
                .filter((b) =>
                  toBook.some(
                    (combo) =>
                      b.court === combo.court &&
                      b.date === combo.iso &&
                      toMinHHMM(b.startTime) < eM &&
                      toMinHHMM(b.endTime) > sM,
                  ),
                )
                .map((b) => b.id);
            }

            // Build the whole list and create in ONE batch (one Storage write + one
            // re-render) instead of going via onAddBooking in a loop — large
            // series would otherwise hang the browser. recur is stored on each booking
            // so the pattern can be edited later.
            const recurMeta = isSeries && recurPreview.recur ? { recur: recurPreview.recur } : {};
            const dataList = toBook.map((combo) => ({
              ...base,
              ...(seriesId ? { seriesId } : {}),
              ...recurMeta,
              court: combo.court,
              date: combo.iso,
            }));
            // DEVIATION (lint): `cancelled` split out as `const` — it is never
            // reassigned (prefer-const), while `created`/`failed` are. Otherwise
            // byte-identical to the original artifact.
            let created = 0,
              failed = 0;
            const cancelled = deleteIds.length;
            if (onAddManyBookings) {
              const r = await onAddManyBookings(dataList, { adminOverride: true, deleteIds });
              // A BOOKING IN THE WAY IS PROTECTED (#1374): paid, a payment
              // open or a pay link outstanding. Nothing was cancelled or
              // created, so say that — not «created 0, cancelled N» — and
              // leave the add dialog open for another choice.
              if (r.refusedBookings) {
                setRecurPreview(null);
                alert(r.error);
                return;
              }
              created = r.created || 0;
              failed = r.failed || 0;
            } else {
              // Fallback if the batch function is not available.
              for (const id of deleteIds) await onDeleteBooking(id);
              for (const data of dataList) {
                // No prompt inside a loop: a recurring series across a broken
                // court would ask once per week. A batch that hits one is
                // reported in the counts, and the operator retries the ones
                // they meant to (#271).
                const r = await onAddBooking(data, {
                  adminOverride: true,
                  promptOverride: false,
                });
                if (r.success) created++;
                else failed++;
              }
            }
            setRecurPreview(null);
            setShowAddModal(false);
            setAddInitial(null);
            let msg = t(
              created === 1 ? 'booking.view.alert.createdOne' : 'booking.view.alert.createdMany',
              { n: String(created) },
            );
            if (cancelled > 0)
              msg += t('booking.view.alert.cancelledExisting', { n: String(cancelled) });
            if (failed > 0) msg += t('booking.view.alert.failedCount', { n: String(failed) });
            alert(msg);
          };
          return (
            // Opens OVER the add modal, which stays mounted beneath it — so it
            // is the innermost dialog and owns Escape and Tab until it closes,
            // when the add modal gets them back (#900).
            <Dialog
              labelledBy={ids.recurTitle}
              describedBy={ids.recurCounts}
              onClose={() => setRecurPreview(null)}
              showClose={false}
              backdropClassName=""
              backdropStyle={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                background: 'rgba(15,22,17,0.55)',
              }}
              className=""
              style={{
                background: '#fff',
                maxWidth: 480,
                width: '100%',
                maxHeight: 'calc(100dvh - 100px)',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #e7eae3',
                  background: '#fafbf7',
                }}
              >
                <h3 id={ids.recurTitle} className="font-display text-lg font-semibold">
                  {t('booking.view.confirm.title')}
                </h3>
                <p id={ids.recurCounts} className="text-xs text-ink-3 mt-0.5">
                  {t('booking.view.confirm.counts', {
                    total: String(combos.length),
                    free: String(clean.length),
                    conflicts: String(conflicts.length),
                  })}
                </p>
              </div>
              <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
                {conflicts.length > 0 ? (
                  <>
                    <div className="mb-2 px-3 py-2 rounded-lg bg-rose-soft/40 border border-rose-200 text-rose-900 text-xs">
                      <strong>{conflicts.length}</strong>{' '}
                      {t('booking.view.confirm.conflictsAfter', {
                        total: String(combos.length),
                      })}
                    </div>
                    <div className="space-y-1 mb-3">
                      {conflicts.map((c, i) => {
                        const d = new Date(c.iso + 'T00:00:00');
                        const wd = weekdayLower[d.getDay()];
                        const cw = c.conflictWith || {};
                        return (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded-lg bg-rose-soft/20 border border-rose-100"
                          >
                            <span className="text-ink-1 flex-shrink-0">
                              {wd} {d.getDate()}.{d.getMonth() + 1} · {navn(c.court)}
                            </span>
                            <span className="text-rose-700 font-medium text-right truncate">
                              ↔ {cw.who}
                              {cw.time ? ` (${cw.time})` : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs">
                    {t('booking.view.confirm.noConflicts', { n: String(combos.length) })}
                  </div>
                )}
              </div>
              <div
                style={{
                  padding: '12px 20px',
                  borderTop: '1px solid #e7eae3',
                  background: '#fafbf7',
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  onClick={() => setRecurPreview(null)}
                  className="text-sm px-3 py-2 rounded-lg text-ink-2 hover:bg-bg-soft"
                >
                  {t('booking.cancel')}
                </button>
                {conflicts.length > 0 && (
                  <button
                    onClick={() => doCreate('skip')}
                    className="text-sm px-4 py-2 rounded-lg bg-emerald-700 text-white font-semibold hover:bg-emerald-800"
                  >
                    {t('booking.view.confirm.skip', {
                      skip: String(conflicts.length),
                      book: String(clean.length),
                    })}
                  </button>
                )}
                {conflicts.length > 0 ? (
                  <button
                    onClick={() => doCreate('all')}
                    className="text-sm px-4 py-2 rounded-lg border-2 border-amber-300 text-amber-800 font-semibold hover:bg-amber-50"
                  >
                    {t('booking.view.confirm.override', { n: String(conflicts.length) })}
                  </button>
                ) : (
                  <button
                    onClick={() => doCreate('all')}
                    className="text-sm px-4 py-2 rounded-lg bg-emerald-700 text-white font-semibold hover:bg-emerald-800"
                  >
                    {t('booking.view.confirm.createAll', { n: String(combos.length) })}
                  </button>
                )}
              </div>
            </Dialog>
          );
        })()}

      {/* Price zone editor */}
      {showZoneEditor && (
        <BookingZoneEditor
          zones={zones}
          defaultPricePerHour={cfg.defaultPricePerHour || 0}
          guestDefaultPricePerHour={guestPrices?.guestDefaultPricePerHour ?? null}
          config={cfg}
          onClose={() => setShowZoneEditor(false)}
          onSave={async (newZones, newDefaultPrice, newGuestDefault) => {
            // EVERY READ STATE FIRST, whatever changed (#1362, and the #1363
            // review's gap). This editor can write three documents; if any of
            // them is unread nothing is written, so a save is never half done
            // and never goes out without a precondition. Checked here, not only
            // by the button: the editor can stay open while a document becomes
            // unread, and a zone-only save checked only the zones before.
            if (!pricesRead) {
              alert(t('booking.view.configUnread.saveRefused'));
              return;
            }
            // ONE DOCUMENT AT A TIME, AND STOP AT THE FIRST FAILURE (#1370).
            // There is no transaction across the three, so the save cannot be
            // atomic; it is explicit instead. Nothing after a failed write is
            // sent, and the message says what is KNOWN: saved, proven not
            // saved, unknown (a 5xx or a lost answer may have landed), not
            // attempted. A failed write leaves its document unread, so the
            // banner offers «Last inn på nytt», which also settles an unknown.
            const steps: PriceSaveStep[] = [];
            // THE DEFAULT PRICE FIRST, and only that field if it changed: it is
            // the one field of the venue setup this editor owns (DOMAIN_RULES
            // § 7). `cfg` is merged over the engine defaults, so sending it
            // would store 07:00–23:00 as saved hours and open booking (#1357,
            // #1192).
            if (newDefaultPrice !== (cfg.defaultPricePerHour || 0) && onUpdateConfig) {
              steps.push({
                label: t('booking.zones.defaultPrice'),
                write: () => onUpdateConfig({ defaultPricePerHour: newDefaultPrice }),
              });
            }
            steps.push({ label: t('booking.zones.title'), write: () => onUpdateZones(newZones) });
            // THE GUEST DEFAULT (#833), in its own document, only when it moved —
            // so a club that never touches guest prices never writes the key.
            // A refusal (a role without `booking.rules.manage`) is said, not lost.
            if (
              onUpdateGuestPrices &&
              newGuestDefault !== (guestPrices?.guestDefaultPricePerHour ?? null)
            ) {
              steps.push({
                label: t('booking.zones.guestDefaultPrice'),
                write: () => onUpdateGuestPrices({ guestDefaultPricePerHour: newGuestDefault }),
              });
            }
            const stopped = await runPriceSave(steps);
            if (stopped) alert(priceSaveFailureMessage(stopped, t));
            else setShowZoneEditor(false);
          }}
        />
      )}

      {/* Settings — buffer, opening hours, durations, courts */}
      {showConfigEditor && (
        <BookingConfigEditor
          config={cfg}
          totalCourts={totalCourts}
          hoursSaved={bookingHoursSaved}
          onClose={() => setShowConfigEditor(false)}
          onSave={async (newCfg) => {
            const r = await onUpdateConfig(newCfg);
            if (r.success) setShowConfigEditor(false);
            else if (isBookingNotRead(r.code)) alert(t('booking.view.configUnread.saveRefused'));
            else alert(t('booking.view.alert.saveFailed', { error: String(r.error) }));
          }}
        />
      )}

      {/* Equipment registry — admin (docs/domains/ASSET_BOOKING.md) */}
      {showAssetEditor && onSaveAsset && onDeleteAsset && (
        <AssetEditor
          assets={assets}
          config={cfg}
          onClose={() => setShowAssetEditor(false)}
          onSave={onSaveAsset}
          onDelete={onDeleteAsset}
        />
      )}

      {/* One equipment booking — standalone or attached to a court booking */}
      {assetBookingModal && onAddAssetBooking && (
        <AssetBookingModal
          mode={assetBookingModal.mode}
          initial={assetBookingModal.initial}
          assets={assets}
          assetBookings={assetBookings}
          bookings={bookings}
          config={cfg}
          adminMode={adminMode}
          defaultDate={selectedDate}
          onClose={() => setAssetBookingModal(null)}
          onSave={async (intent) => {
            const editingId =
              assetBookingModal.mode === 'edit' ? assetBookingModal.initial?.id : null;
            const r =
              editingId && onUpdateAssetBooking
                ? await onUpdateAssetBooking(editingId, intent)
                : await onAddAssetBooking(intent);
            if (r?.success) setAssetBookingModal(null);
            return r;
          }}
          onDelete={
            assetBookingModal.mode === 'edit' &&
            assetBookingModal.initial?.id &&
            onDeleteAssetBooking
              ? async () => {
                  const r = await onDeleteAssetBooking(assetBookingModal.initial.id);
                  if (r?.success) setAssetBookingModal(null);
                  return r;
                }
              : null
          }
        />
      )}

      {/* Help page — explanation of the booking rules */}
      {showHelp && <BookingHelpModal config={cfg} onClose={() => setShowHelp(false)} />}

      {/* Fake payment modal — shown after the add modal when price > 0 and not admin mode */}
      {pendingPayment && (
        <BookingPaymentModal
          amount={pendingPayment.priceNok}
          bookingSummary={t('booking.view.paymentSummary', {
            court: String(pendingPayment.data.court),
            date: pendingPayment.data.date,
            start: pendingPayment.data.startTime,
            end: fromMinHHMM(
              toMinHHMM(pendingPayment.data.startTime) + (pendingPayment.data.durationMin || 90),
            ),
          })}
          onCancel={() => {
            setPendingPayment(null);
            setAddInitial(null);
          }}
          onSuccess={async (paymentInfo) => {
            const r = await onAddBooking({
              ...pendingPayment.data,
              paymentStatus: 'paid',
              priceNok: pendingPayment.priceNok,
              payment: paymentInfo, // { method, transactionId, paidAt, amount }
            });
            if (r.success) {
              const bookingId = r.booking?.id || r.id;
              await attachAssets(bookingId, pendingPayment.data, pendingPayment.assetIds);
              if (serieContext?.onBooked && bookingId)
                serieContext.onBooked(bookingId, pendingPayment.data);
              if (pendingPayment.plannedMatch && bookingId && onRegisterPlannedMatch) {
                try {
                  await onRegisterPlannedMatch({ ...pendingPayment.plannedMatch, bookingId });
                } catch (e) {
                  console.error('[Padel24] Could not register the planned match:', e);
                }
              }
              // booker_all single-match activity: create it now the court is paid.
              if (pendingPayment.activity && bookingId && onCreateLinkedActivity) {
                try {
                  await onCreateLinkedActivity({
                    activityId: pendingPayment.activity.activityId,
                    config: pendingPayment.activity.buildCfg(bookingId),
                  });
                } catch (e) {
                  console.error('[Padel24] Could not create the single-match activity:', e);
                }
              }
              setPendingPayment(null);
              setAddInitial(null);
            } else {
              // Should not happen (we already validated in the modal), but handle it anyway
              alert(t('booking.view.alert.paidButNotSaved', { error: String(r.error) }));
              setPendingPayment(null);
            }
          }}
        />
      )}
    </div>
  );
}
